// web/src/pages/Home.tsx
// frame.sandstonecloud.com — Full web scoring app
// Cross-platform: iOS Safari, Chrome Android, desktop browsers
// Layout: Mode → Weights → Chat Box → CTA (no overlap, clear separation)
import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { scorePhotosWeb, type WebScoringOptions } from '../scoring/webScorer';
import {
  SCORING_MODES, DIMENSIONS, NOVEL_DIMENSIONS, TIER_ENTITLEMENTS, SubscriptionTier,
  type ScoringModeId, type PhotoScore, type WeightProfile,
  assignTier,
} from '@frame/scoring-types';
import { formatScore, percentileLabel, parseVibeV2 } from '@frame/utils';
import { WebAuthService, type AuthState } from '../services/auth';
import { UsageTracker } from '../services/usageTracker';
import { ChatService, type ChatSession } from '../services/chatService';
import { ChatPanel } from '../components/ChatPanel';
import { PrismStore } from '../services/prismStore';
import { PrismTracker } from '../services/prismTracker';
import { EngramService } from '../services/engramService';
import { applyPrismModifier, getConfidenceLabel } from '@frame/utils';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import { FavoritesStore, type FavoritePhoto } from '../services/favoritesStore';

// ─── Admin check via Cognito group membership ─────────────
// Admin credentials are NO LONGER hardcoded in the client bundle.
// Admin users must be added to the 'frame-admins' Cognito group.
async function checkAdminGroup(): Promise<boolean> {
  try {
    const session = await WebAuthService.getSession();
    const groups = (session?.tokens?.idToken?.payload?.['cognito:groups'] as string[] | undefined) ?? [];
    if (groups.includes('frame-admins')) return true;
    const email = (session?.tokens?.idToken?.payload?.email as string) ?? '';
    return email.toLowerCase().endsWith('@sandstonecloud.com');
  } catch {
    return false;
  }
}

// ─── Design tokens (Sandstone Cloud dark navy) ─────────────
const C = {
  bg: '#0f172a', text: '#f8fafc',
  teal: '#50B8A8', tealLight: 'rgba(80,184,168,0.12)', tealMedium: 'rgba(80,184,168,0.22)',
  tealHover: '#3D9E8F', mid: '#3B8CB8',
  blue: '#5186B0', blueLight: 'rgba(81,134,176,0.12)',
  navy: '#1A2E44', navySoft: '#2D4A66',
  surface: '#1e293b', secondary: '#94a3b8', divider: '#334155',
  error: 'rgba(239, 68, 68, 0.7)', white: '#FFFFFF',
  surfaceHover: '#334155', muted: '#64748b',
  success: '#059669',
} as const;

/** v2 score-color coding using tier colors */
function scoreColor(score: number): string {
  return assignTier(score).color;
}

// ─── Inline SVG Icons (brand-consistent, no external deps) ──
const Icon = {
  camera: (size = 36, color: string = C.teal) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="14" rx="3" />
      <path d="M8.5 6V5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  ),
  lock: (size = 12, color: string = C.muted) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  ),
  star: (size = 14, color: string = C.teal) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  ),
  shield: (size = 14, color: string = C.secondary) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  mapPin: (size = 14, color: string = C.teal) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  warning: (size = 14, color: string = '#ef4444') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  close: (size = 16, color: string = 'currentColor') => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  sparkles: (size = 14, color: string = C.teal) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      <path d="M5 19l1 2.5L8.5 20l-2.5 1L5 19z" />
      <path d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14z" />
    </svg>
  ),
  bolt: (size = 14, color: string = C.teal) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  download: (size = 16, color: string = C.muted) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  heart: (size = 16, color: string = C.muted, filled = false) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  shareArrow: (size = 16, color: string = C.muted) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  ),
  thumbsDown: (size = 16, color: string = C.muted) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
    </svg>
  ),
  prism: (size = 14, color: string = C.teal) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M12 2L2 19h20L12 2z" /><path d="M12 2v17" /><path d="M2 19l10-7" /><path d="M22 19l-10-7" />
    </svg>
  ),
  gear: (size = 16, color: string = C.secondary) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

const MODE_ORDER: ScoringModeId[] = [
  'balanced', 'social', 'instagram', 'professional', 'travel', 'realEstate', 'event', 'sports', 'custom',
];

// ─── Responsive helpers ─────────────────────────────────────
// CSS custom properties injected once for viewport units
const injectCSS = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('frame-web-css')) return;
  const style = document.createElement('style');
  style.id = 'frame-web-css';
  style.textContent = `
    /* Fix iOS 100vh bug — use dvh where supported */
    :root {
      --vh: 1vh;
      --safe-top: env(safe-area-inset-top, 0px);
      --safe-bottom: env(safe-area-inset-bottom, 0px);
      --safe-left: env(safe-area-inset-left, 0px);
      --safe-right: env(safe-area-inset-right, 0px);
    }
    @supports (height: 1dvh) {
      :root { --vh: 1dvh; }
    }
    /* Prevent rubber-banding scroll on iOS */
    html, body { overscroll-behavior: none; }
    /* Smooth momentum scrolling on iOS */
    .frame-scroll { -webkit-overflow-scrolling: touch; }
    /* Prevent text selection on interactive elements */
    .frame-chip { -webkit-user-select: none; user-select: none; }
    /* Fix Android Chrome address bar resize flicker */
    @supports (height: 100dvh) {
      .frame-page { min-height: 100dvh; }
    }
    /* Focus ring for accessibility */
    .frame-focus:focus-visible {
      outline: 2px solid ${C.teal};
      outline-offset: 2px;
      border-radius: 8px;
    }
    /* Textarea iOS fix — prevent zoom, proper resize */
    textarea.frame-vibe {
      font-size: 16px !important; /* iOS zoom prevention */
      -webkit-appearance: none;
      appearance: none;
    }
    /* Scrollbar styling */
    .frame-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
    .frame-scroll::-webkit-scrollbar-track { background: transparent; }
    .frame-scroll::-webkit-scrollbar-thumb { background: ${C.surfaceHover}; border-radius: 2px; }
    /* Animations */
    @keyframes frameSlideUp {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes frameFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes frameBarGrow {
      from { width: 0%; }
    }
    .frame-animate-in {
      animation: frameSlideUp 0.4s ease-out forwards;
    }
    .frame-fade-in {
      animation: frameFadeIn 0.3s ease-out forwards;
    }
    .frame-bar-animate {
      animation: frameBarGrow 0.6s ease-out forwards;
    }
    /* Mode chip hover */
    .frame-chip:hover { transform: scale(0.97); }
    .frame-chip:active { transform: scale(0.95); }
    /* Pull-to-refresh spinner */
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    /* Shimmer animation */
    @keyframes frameShimmer {
      0% { background-position: 200% center; }
      100% { background-position: -200% center; }
    }
    .frame-shimmer {
      background-size: 200% auto;
      animation: frameShimmer 3s linear infinite;
    }
    /* CTA glisten animation — subtle brightness pulse */
    @keyframes frameGlisten {
      0%, 100% { filter: brightness(1); }
      50% { filter: brightness(1.12); }
    }
    .frame-cta { animation: frameGlisten 3s ease-in-out infinite; }
    .frame-cta:disabled { animation: none; }
    /* CTA hover — teal→blue glow */
    .frame-cta:hover:not(:disabled) { transform: scale(1.01); box-shadow: 0 4px 24px rgba(80,184,168,0.40), 0 0 40px rgba(81,134,176,0.20); }
    .frame-cta:active:not(:disabled) { transform: scale(0.99); }
    /* Photo card hover */
    .frame-photo:hover { transform: scale(1.03); z-index: 2; }
    /* Modal backdrop animation */
    .frame-modal-backdrop { animation: frameFadeIn 0.2s ease-out; }
  `;
  document.head.appendChild(style);
};

// ─── Main App ───────────────────────────────────────────────
export function Home() {
  const navigate = useNavigate();
  const [showLanding, setShowLanding] = useState(true);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [selectedMode, setSelectedMode] = useState<ScoringModeId>('balanced');
  const [vibeText, setVibeText] = useState('');
  const [requestedCount, setRequestedCount] = useState(25);
  const [countInput, setCountInput] = useState('25');
  const [scoring, setScoring] = useState(false);
  const [scoringError, setScoringError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<PhotoScore[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoScore | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const vibeRef = useRef<HTMLTextAreaElement>(null);

  // Auth + usage state
  const [authState, setAuthState] = useState<AuthState>({ isAuthenticated: false, user: null, isLoading: true });
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showUpgradeNudge, setShowUpgradeNudge] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'verify' | 'forgot' | 'forgot-verify' | 'profile'>('signup');
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [profileInput, setProfileInput] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authNewPassword, setAuthNewPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Subscription tier + admin
  const [userTier, setUserTier] = useState<SubscriptionTier>(SubscriptionTier.Free);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionPhotosScored, setSessionPhotosScored] = useState<Record<string, number>>({});

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [inactivityWarning, setInactivityWarning] = useState(false);

  // Feedback modal state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackFiles, setFeedbackFiles] = useState<File[]>([]);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const feedbackFileRef = useRef<HTMLInputElement>(null);

  // PRISM — Adaptive Scoring state
  const [prismEnabled, setPrismEnabled] = useState(() => PrismStore.isEnabled());
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [showFavorites, setShowFavorites] = useState(false);
  const [favPhotos, setFavPhotos] = useState<FavoritePhoto[]>([]);
  const [favThumbnails, setFavThumbnails] = useState<Record<string, string>>({});
  const [showPrismSettings, setShowPrismSettings] = useState(false);
  const [prismToast, setPrismToast] = useState<string | null>(null);
  const [prismPaused, setPrismPaused] = useState(() => PrismStore.isPaused());
  const [enrollmentDismissed, setEnrollmentDismissed] = useState(false);

  // ─── Pull-to-refresh state ──────────────────────────────
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullStartY = useRef(-1);
  const lastPullTime = useRef(0);
  const pullCountRef = useRef(0);
  const PULL_THRESHOLD = 80;
  const DOUBLE_PULL_WINDOW = 2000;

  // ─── State caching (survives page refresh) ────────────────
  useEffect(() => {
    const cached = sessionStorage.getItem('frame_mode');
    if (cached) setSelectedMode(cached as ScoringModeId);
    const cachedVibe = sessionStorage.getItem('frame_vibeText');
    if (cachedVibe) setVibeText(cachedVibe);
    const cachedCount = sessionStorage.getItem('frame_count');
    if (cachedCount) { setCountInput(cachedCount); setRequestedCount(parseInt(cachedCount, 10) || 25); }
    const cachedResults = sessionStorage.getItem('frame_results');
    if (cachedResults) {
      try { setResults(JSON.parse(cachedResults)); } catch { /* ignore corrupt cache */ }
    }
  }, []);

  useEffect(() => { sessionStorage.setItem('frame_mode', selectedMode); }, [selectedMode]);
  useEffect(() => { sessionStorage.setItem('frame_vibeText', vibeText); }, [vibeText]);
  useEffect(() => { sessionStorage.setItem('frame_count', countInput); }, [countInput]);
  useEffect(() => {
    if (results.length > 0) {
      // Cache results without blob URLs (they're ephemeral)
      const cacheable = results.map(r => ({ ...r, thumbnailUri: '' }));
      try { sessionStorage.setItem('frame_results', JSON.stringify(cacheable)); } catch { /* quota exceeded */ }
    }
  }, [results]);

  // Tier entitlements for current user
  const entitlements = TIER_ENTITLEMENTS[userTier];

  // ─── Inactivity timer (30 min → auto sign-out) ─────────
  const handleInactivityTimeout = useCallback(async () => {
    setInactivityWarning(false);
    await WebAuthService.signOut();
    setAuthState({ isAuthenticated: false, user: null, isLoading: false });
    setDisplayName(null);
    WebAuthService.clearDisplayNameCache();
    setUserTier(SubscriptionTier.Free);
    setIsAdmin(false);
    setChatOpen(false);
    setActiveSessionId(null);
    setChatSessions([]);
    // Clear any open auth form state
    setShowAuthGate(false);
    setAuthEmail('');
    setAuthPassword('');
    setAuthNewPassword('');
    setAuthCode('');
    setAuthError('');
    setAuthSuccess('');
    setAuthMode('signup');
    setProfileInput('');
  }, []);

  const handleInactivityWarning = useCallback(() => {
    setInactivityWarning(true);
  }, []);

  // Dismiss inactivity warning on any user interaction
  useEffect(() => {
    if (!inactivityWarning) return;
    const dismiss = () => setInactivityWarning(false);
    const events: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'click', 'touchstart'];
    // Delay listener attachment slightly so the warning renders first
    const id = setTimeout(() => {
      events.forEach(e => window.addEventListener(e, dismiss, { once: true, passive: true }));
    }, 500);
    // Also auto-dismiss after 60 seconds if no interaction
    const autoId = setTimeout(dismiss, 60_000);
    return () => {
      clearTimeout(id);
      clearTimeout(autoId);
      events.forEach(e => window.removeEventListener(e, dismiss));
    };
  }, [inactivityWarning]);

  useInactivityTimer({
    timeoutMs: 30 * 60 * 1000, // 30 minutes
    onTimeout: handleInactivityTimeout,
    onWarning: handleInactivityWarning,
    enabled: authState.isAuthenticated,
  });

  // Check auth on mount
  useEffect(() => {
    let mounted = true;
    if (!WebAuthService.isConfigured()) {
      setAuthState({ isAuthenticated: false, user: null, isLoading: false });
      return;
    }
    (async () => {
      try {
        const user = await WebAuthService.getCurrentUser();
        if (!mounted) return;
        if (user) {
          setAuthState({ isAuthenticated: true, user: { userId: user.userId, email: user.signInDetails?.loginId ?? '' }, isLoading: false });
          const admin = await checkAdminGroup();
          if (mounted && admin) { setIsAdmin(true); setUserTier(SubscriptionTier.Gold); }
          const name = await WebAuthService.getDisplayName();
          if (mounted && name) setDisplayName(name);
          // Load existing chat sessions, or create a new one if none exist
          if (mounted) {
            const sessions = ChatService.getSessions(user.userId);
            if (sessions.length > 0) {
              setChatSessions(sessions);
              setActiveSessionId(sessions[0].sessionId);
            } else {
              const newSession = ChatService.createSession(user.userId);
              setChatSessions([newSession]);
              setActiveSessionId(newSession.sessionId);
            }
          }
        } else {
          setAuthState({ isAuthenticated: false, user: null, isLoading: false });
        }
      } catch {
        if (mounted) setAuthState({ isAuthenticated: false, user: null, isLoading: false });
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Inject CSS once on mount
  useEffect(() => {
    injectCSS(); UsageTracker.init();
    // Load persisted favorites from IndexedDB
    FavoritesStore.getIds().then(ids => { if (ids.size > 0) setFavorites(ids); });
  }, []);

  // Cleanup preview URLs
  useEffect(() => {
    return () => previews.forEach(URL.revokeObjectURL);
  }, [previews]);

  // Cleanup result thumbnail URLs
  useEffect(() => {
    return () => results.forEach(r => { if (r.thumbnailUri) URL.revokeObjectURL(r.thumbnailUri); });
  }, [results]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedPhoto) {
      document.body.style.overflow = 'hidden';
      // iOS Safari scroll lock
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [selectedPhoto]);

  // Close modals on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showFeedback) setShowFeedback(false);
        else if (chatOpen) setChatOpen(false);
        else if (selectedPhoto) setSelectedPhoto(null);
        else if (showAuthGate) { setShowAuthGate(false); setAuthPassword(''); setAuthNewPassword(''); setAuthCode(''); setAuthError(''); setAuthSuccess(''); setAuthMode('signup'); }
        else if (showUpgrade) setShowUpgrade(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedPhoto, showAuthGate, showUpgrade, showFeedback, chatOpen]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;

    // Validate file types at runtime (accept attribute is just a hint)
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    const validFiles = selected.filter(f => validTypes.includes(f.type) || f.name.match(/\.(jpe?g|png|webp|heic)$/i));
    if (validFiles.length === 0) return;

    setFiles(validFiles);
    const urls = validFiles.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    setSelectedPhoto(null);
    setScoringError(null);
    setShowUpgradeNudge(false);
    setShowLanding(false);
  }, []);

  const handleScore = useCallback(async () => {
    if (files.length === 0) return;

    // Auth gate: require account after 25 free photos
    if (!authState.isAuthenticated && UsageTracker.requiresAuth()) {
      setAuthError(''); setAuthSuccess(''); setAuthMode('signup'); setShowAuthGate(true);
      return;
    }

    // Premium mode gate
    const mode = SCORING_MODES[selectedMode];
    if (mode.requiresPremium && userTier === SubscriptionTier.Free) {
      setShowUpgrade(true);
      return;
    }

    setScoring(true);
    setScoringError(null);
    // Clear previous results only when scoring actually starts (not on file selection)
    // so chat retains photo context until new scores replace them
    results.forEach(r => { if (r.thumbnailUri) URL.revokeObjectURL(r.thumbnailUri); });
    setResults([]);
    setProgress({ current: 0, total: files.length });

    // PRISM: Set session ID for behavioral event tracking
    PrismTracker.setSession(`web_${Date.now()}`, authState.user?.userId);

    try {
      // Use vibe parser for custom weights when user provides vibe text
      let weights: WeightProfile = mode.defaultWeights;
      let novelHints: Partial<Record<string, number>> | undefined;
      if (vibeText.trim()) {
        try {
          const parsed = parseVibeV2(vibeText, selectedMode);
          const vc = parsed.vibeConfig;
          weights = {
            ...mode.defaultWeights,
            portraitWeight: vc.portraitWeight,
            lightingWeight: vc.lightingWeight,
            compositionWeight: vc.compositionWeight,
            sceneWeight: vc.sceneWeight,
          };
          if (Object.keys(parsed.novelHints).length > 0) {
            novelHints = parsed.novelHints;
          }
        } catch {
          // Fall back to default weights if parsing fails
        }
      }

      // Build PRISM state for personalized scoring
      const prismState = prismEnabled ? {
        enabled: true,
        preferenceVector: PrismStore.getPreferenceVector(),
        ownerBaseline: PrismStore.getOwnerBaseline(),
      } : undefined;

      const options: WebScoringOptions = {
        weights,
        modeId: selectedMode as string,
        onProgress: (current, total) => setProgress({ current, total }),
        prismState,
        novelHints,
      };

      const scores = await scorePhotosWeb(files, options);
      setResults(scores);

      // Adaptive threshold: auto-set keepCount based on total scored photos
      const totalCount = scores.length;
      const autoKeep = totalCount <= 3 ? totalCount
        : totalCount <= 10 ? Math.max(3, Math.round(totalCount * 0.80))
        : totalCount <= 25 ? Math.max(3, Math.round(totalCount * 0.70))
        : totalCount <= 100 ? Math.max(3, Math.round(totalCount * 0.55))
        : Math.max(3, Math.round(totalCount * 0.40));
      setRequestedCount(autoKeep);

      // PRISM: Store aesthetic properties for preference learning
      if (prismEnabled) {
        const aestheticsToStore = scores
          .filter(s => s.prismAesthetics)
          .map(s => ({ id: s.id, aesthetics: s.prismAesthetics! }));
        if (aestheticsToStore.length > 0) {
          PrismStore.storeSessionAesthetics(aestheticsToStore);
        }
        // Run preference learning from accumulated behavioral data
        PrismTracker.learnFromSession();
      }

      // Track mode diversity for Engram "Photo Curation Pro" goal
      if (EngramService.isEnabled()) {
        EngramService.updateGoalProgress('photo_curation_pro', 1, true).catch(() => {});
      }

      // Track per-mode session usage
      setSessionPhotosScored(prev => ({
        ...prev,
        [selectedMode]: (prev[selectedMode] || 0) + files.length,
      }));

      // Track usage for free-tier gating
      if (!authState.isAuthenticated) {
        UsageTracker.recordSession(files.length);
      }

      // Gentle nudge for free users (non-blocking)
      if (userTier === SubscriptionTier.Free && selectedMode !== 'balanced') {
        setShowUpgradeNudge(true);
      }

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } catch (err: any) {
      console.error('[FRAME Web] Scoring error:', err);
      setScoringError(err?.message || 'Failed to score photos. Please try again.');
      setProgress({ current: 0, total: 0 });
    } finally {
      setScoring(false);
    }
  }, [files, selectedMode, vibeText, authState.isAuthenticated, userTier, results]);

  // All photos (including non-photos like memes/screenshots) are scored on merit
  const qualityFloorExcluded = 0;
  const selectedCount = useMemo(
    () => Math.min(requestedCount, results.length),
    [requestedCount, results.length],
  );

  const allComposites = useMemo(
    () => results.map((r) => r.compositeScore),
    [results],
  );

  const currentMode = SCORING_MODES[selectedMode];
  const modeWeights = [
    currentMode.defaultWeights.portraitWeight,
    currentMode.defaultWeights.lightingWeight,
    currentMode.defaultWeights.compositionWeight,
    currentMode.defaultWeights.sceneWeight,
  ];

  // ─── Pull-to-refresh handlers ─────────────────────────
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (window.scrollY <= 0 && e.touches.length === 1) {
      pullStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (pullStartY.current < 0 || window.scrollY > 0) return;
    const delta = e.touches[0].clientY - pullStartY.current;
    if (delta > 0) {
      setPullDistance(Math.min(delta * 0.5, 120)); // Damped pull
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD) {
      const now = Date.now();
      if (now - lastPullTime.current < DOUBLE_PULL_WINDOW) {
        pullCountRef.current++;
      } else {
        pullCountRef.current = 1;
      }
      lastPullTime.current = now;

      if (pullCountRef.current >= 2) {
        // Double pull: clear session
        setFiles([]); setPreviews([]); setResults([]); setSelectedPhoto(null);
        setVibeText(''); setCountInput('25'); setRequestedCount(25);
        setProgress({ current: 0, total: 0 }); setScoringError(null);
        setShowLanding(true);
        sessionStorage.removeItem('frame_results');
        sessionStorage.removeItem('frame_vibeText');
        pullCountRef.current = 0;
        setIsRefreshing(true);
        setTimeout(() => setIsRefreshing(false), 600);
      } else {
        // Single pull: refresh (re-render, scroll to top)
        setIsRefreshing(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setIsRefreshing(false), 600);
      }
    }
    setPullDistance(0);
    pullStartY.current = -1;
  }, [pullDistance]);

  return (
    <div
      className="frame-page"
      style={S.page}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {(pullDistance > 10 || isRefreshing) && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
          display: 'flex', flexDirection: 'column' as const, justifyContent: 'center', alignItems: 'center',
          height: Math.min(pullDistance, 60),
          transition: pullDistance === 0 ? 'height 0.3s ease' : 'none',
          overflow: 'hidden',
        }}>
          <div
            role="status"
            aria-label={isRefreshing ? 'Refreshing' : 'Pull to refresh'}
            style={{
              width: 24, height: 24, borderRadius: '50%',
              border: `2px solid ${C.teal}`,
              borderTopColor: 'transparent',
              animation: isRefreshing ? 'spin 0.6s linear infinite' : 'none',
              transform: `rotate(${pullDistance * 3}deg)`,
              opacity: Math.min(1, pullDistance / PULL_THRESHOLD),
            }}
          />
          {pullCountRef.current >= 1 && pullDistance >= PULL_THRESHOLD && (
            <span style={{ fontSize: 10, color: C.secondary, marginTop: 4 }}>
              Pull again to clear
            </span>
          )}
        </div>
      )}
      {/* ─── Header ─── */}
      <header style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => { setResults([]); setSelectedPhoto(null); setFiles([]); setPreviews([]); setProgress({ current: 0, total: 0 }); setShowLanding(true); window.scrollTo(0, 0); }}>
          <img src="/logo.png" alt="FRAME" style={{ height: 36, width: 36, borderRadius: 8 }} />
          <div>
            <h1 style={S.brand}>FRAME</h1>
            <p style={S.brandSub}>AI Photo Curation</p>
          </div>
        </div>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px, 2vw, 16px)', flexWrap: 'wrap' as const, justifyContent: 'flex-end' }}>
          {authState.isAuthenticated ? (
            <>
              {isAdmin && (
                <button
                  onClick={() => window.open('/admin', '_blank')}
                  style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const,
                    background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    border: `1px solid ${C.teal}40`, borderRadius: 6, padding: '3px 8px',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                  title="Open Admin Panel"
                >
                  ADMIN
                </button>
              )}
              {userTier !== SubscriptionTier.Free && (
                <span style={{ fontSize: 11, color: userTier === SubscriptionTier.Gold ? C.teal : '#94a3b8', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const }}>
                  {userTier === SubscriptionTier.Gold ? <>{Icon.star(11, C.teal)} GOLD</> : 'SILVER'}
                </span>
              )}
              <span style={{ fontSize: 13, color: C.secondary }}>
                {displayName || authState.user?.email?.split('@')[0]}
              </span>
              {userTier === SubscriptionTier.Free && (
                <button onClick={() => setShowUpgrade(true)} style={S.headerLink}>
                  Upgrade
                </button>
              )}
              <button onClick={() => setChatOpen(!chatOpen)} style={{
                ...S.headerLink, display: 'flex', alignItems: 'center', gap: 4,
              }} title="Photo Advisor Chat">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Chat
              </button>
              <button onClick={async () => {
                const all = await FavoritesStore.getAll();
                setFavPhotos(all.sort((a, b) => new Date(b.favoritedAt).getTime() - new Date(a.favoritedAt).getTime()));
                // Create blob URLs for thumbnails
                const thumbs: Record<string, string> = {};
                for (const f of all) {
                  if (f.thumbnailBlob) thumbs[f.id] = URL.createObjectURL(f.thumbnailBlob);
                }
                setFavThumbnails(thumbs);
                setShowFavorites(true);
              }} style={{
                ...S.headerLink, display: 'flex', alignItems: 'center', gap: 4, position: 'relative' as const,
              }} title="My Favorites">
                {Icon.heart(16, favorites.size > 0 ? C.teal : 'currentColor', favorites.size > 0)}
              </button>
              <button onClick={() => setShowPrismSettings(true)} style={{
                ...S.headerLink, display: 'flex', alignItems: 'center', gap: 4,
              }} title="Adaptive Scoring Settings">
                {Icon.gear(16, prismEnabled ? C.teal : 'currentColor')}
              </button>
              <button onClick={async () => {
                try { await WebAuthService.signOut(); } catch (err) { console.error('[FRAME] Sign out error:', err); }
                setAuthState({ isAuthenticated: false, user: null, isLoading: false });
                setUserTier(SubscriptionTier.Free);
                setIsAdmin(false);
                setDisplayName(null);
                WebAuthService.clearDisplayNameCache();
                setSessionPhotosScored({});
                setChatOpen(false);
                setActiveSessionId(null);
                setChatSessions([]);
                setResults([]); setFiles([]); setPreviews([]); setShowLanding(true);
              }} style={S.headerLink}>
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { setAuthMode('login'); setAuthError(''); setAuthSuccess(''); setShowAuthGate(true); }} style={S.headerLink}>
                Log In
              </button>
              <a href="/privacy" style={S.headerLink}>Privacy</a>
            </>
          )}
        </nav>
      </header>

      <main style={S.main} className="frame-scroll">
        {/* Hidden file input — always present so landing CTAs can trigger it */}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          aria-label="Select photos"
        />

        {/* ─── Landing: Non-Authenticated ─── */}
        {showLanding && !authState.isAuthenticated && (
          <>
            <section style={S.hero} className="frame-animate-in">
              <h2 style={S.heroTitle}>
                Find your best photos<br />
                <span style={{ background: 'linear-gradient(135deg, #50B8A8, #5186B0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>with transparent AI scoring</span>
              </h2>
              <p style={S.heroBody}>
                FRAME scores every photo across portrait composition, lighting, composition,
                and scene interest. All processing happens on your device — nothing is uploaded.
              </p>
            </section>

            <section style={S.card} className="frame-animate-in" role="region" aria-label="Features">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
                <div style={{ textAlign: 'center' as const, padding: '20px 12px 16px', borderRadius: 14, backgroundColor: C.tealLight }}>
                  <div style={{ marginBottom: 10 }}>{Icon.star(34, C.teal)}</div>
                  <p style={{ color: C.text, fontSize: 14, fontWeight: 600, margin: '0 0 6px' }}>Transparent Scoring</p>
                  <p style={{ color: C.secondary, fontSize: 12, margin: 0, lineHeight: 1.4 }}>See exactly how every photo scores across 11 dimensions.</p>
                </div>
                <div style={{ textAlign: 'center' as const, padding: '20px 12px 16px', borderRadius: 14, backgroundColor: 'rgba(80,184,168,0.08)' }}>
                  <div style={{ marginBottom: 10 }}>{Icon.camera(34, C.teal)}</div>
                  <p style={{ color: C.text, fontSize: 14, fontWeight: 600, margin: '0 0 6px' }}>9 Scoring Modes</p>
                  <p style={{ color: C.secondary, fontSize: 12, margin: 0, lineHeight: 1.4 }}>Each mode scores what actually matters for that context.</p>
                </div>
                <div style={{ textAlign: 'center' as const, padding: '20px 12px 16px', borderRadius: 14, backgroundColor: C.tealLight }}>
                  <div style={{ marginBottom: 10 }}>{Icon.shield(34, C.teal)}</div>
                  <p style={{ color: C.text, fontSize: 14, fontWeight: 600, margin: '0 0 6px' }}>100% Private</p>
                  <p style={{ color: C.secondary, fontSize: 12, margin: 0, lineHeight: 1.4 }}>All scoring happens on your device. Photos never leave.</p>
                </div>
              </div>

              {/* PRISM Promotion Banner */}
              <div style={{
                padding: '14px 16px', borderRadius: 12, marginBottom: 16,
                background: 'linear-gradient(135deg, rgba(80,184,168,0.12) 0%, rgba(81,134,176,0.12) 100%)',
                border: '1px solid rgba(80,184,168,0.2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ background: 'linear-gradient(135deg, #50B8A8, #5186B0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontSize: 15, fontWeight: 700 }}>PRISM</span>
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(80,184,168,0.2)', color: C.teal, fontWeight: 600 }}>NEW</span>
                </div>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>PRISM Knows You</p>
                <p style={{ color: C.secondary, fontSize: 12, margin: 0, lineHeight: 1.4 }}>
                  PRISM learns how you see. Every photo you favorite, share, or linger on teaches it your aesthetic — so scores feel less like an algorithm and more like a trusted friend who gets your eye. Your visual identity, understood.
                </p>
              </div>

              <button
                className="frame-cta frame-focus"
                onClick={() => fileRef.current?.click()}
                style={{ ...S.scoreCTA, width: '100%', marginTop: 8 }}
              >
                {Icon.camera(16, C.white)}&ensp;Get Started
              </button>
              <p style={{ fontSize: 12, color: C.secondary, textAlign: 'center' as const, marginTop: 8, marginBottom: 0, opacity: 0.8 }}>
                Free to try — no account needed
              </p>

              <p style={{ fontSize: 13, color: C.secondary, textAlign: 'center' as const, marginTop: 14 }}>
                Already have an account?{' '}
                <button
                  onClick={() => { setAuthMode('login'); setAuthError(''); setAuthSuccess(''); setShowAuthGate(true); }}
                  style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}
                >
                  Log in
                </button>
              </p>
            </section>
          </>
        )}

        {/* ─── Landing: Authenticated ─── */}
        {showLanding && authState.isAuthenticated && (
          <section style={S.card} className="frame-animate-in" role="region" aria-label="Welcome back">
            <h2 style={{ ...S.heroTitle, fontSize: 'clamp(22px, 5vw, 28px)', marginBottom: 8 }}>
              Welcome back{displayName ? `, ${displayName}` : ''} {Icon.star(20, C.teal)}
            </h2>
            <p style={{ ...S.heroBody, marginBottom: 24 }}>
              Ready to find your best shots?
            </p>

            {/* Mode selector on landing */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ ...S.sectionLabel, marginBottom: 10 }}>Scoring Mode</h3>
              <div style={S.modeChipScroll} className="frame-scroll">
                <div style={S.modeChipRow}>
                  {MODE_ORDER.map((id) => {
                    const mode = SCORING_MODES[id];
                    const isActive = selectedMode === id;
                    const isLocked = mode.requiresPremium && userTier === SubscriptionTier.Free;
                    return (
                      <button
                        key={id}
                        className="frame-chip frame-focus"
                        onClick={() => {
                          if (isLocked) { setShowUpgrade(true); return; }
                          setSelectedMode(id);
                        }}
                        style={{
                          ...S.modeChip,
                          backgroundColor: isActive ? C.teal : isLocked ? C.surfaceHover : C.surface,
                          color: isActive ? C.white : isLocked ? C.muted : C.text,
                          fontWeight: isActive ? 600 : 500,
                          boxShadow: isActive ? '0 0 10px rgba(80,184,168,0.2), inset 0 1px 0 rgba(255,255,255,0.08)' : 'none',
                          opacity: isLocked ? 0.6 : 1,
                        }}
                      >
                        {isLocked && Icon.lock(12, C.muted)}{isLocked && ' '}{mode.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <button
              className="frame-cta frame-focus"
              onClick={() => fileRef.current?.click()}
              style={{ ...S.scoreCTA, width: '100%' }}
            >
              {Icon.star(16, C.white)}&ensp;Score My Photos
            </button>

            {prismEnabled && (
              <p style={{ fontSize: 12, color: C.teal, textAlign: 'center' as const, marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {Icon.prism(14, C.teal)} Adaptive Scoring active
              </p>
            )}
          </section>
        )}

        {/* ─── Upload Section (scoring view only) ─── */}
        {!showLanding && (
        <section style={S.card} className="frame-animate-in" role="region" aria-label="Photo selection">
          <div
            onClick={() => fileRef.current?.click()}
            style={S.dropzone}
            role="button"
            tabIndex={0}
            className="frame-focus"
            onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
          >
            {files.length > 0 ? (
              <div>
                <p style={S.dropzoneCount}>{files.length} photos selected</p>
                <p style={S.dropzoneHint}>Tap to change selection</p>
                <div style={S.thumbStrip}>
                  {previews.slice(0, 6).map((url, i) => (
                    <img key={i} src={url} alt="" style={S.thumbImg} loading="lazy" />
                  ))}
                  {files.length > 6 && (
                    <div style={S.thumbMore}>+{files.length - 6}</div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div style={S.uploadIcon}>{Icon.camera(36, C.teal)}</div>
                <p style={S.dropzoneTitle}>Select photos to score</p>
                <p style={S.dropzoneHint}>
                  All analysis happens on your device. Your photos never leave your device.
                </p>
              </div>
            )}
          </div>
        </section>
        )}

        {/* ════════════════════════════════════════════════════
            SECTION 1: SCORING MODE + WEIGHT SLIDERS
            This section is ABOVE the chat box — fixed order, no overlap
            ════════════════════════════════════════════════════ */}
        {files.length > 0 && (
          <section style={S.card} className="frame-animate-in" role="region" aria-label="Scoring mode">
            <h3 style={S.sectionLabel}>Scoring Mode</h3>

            {/* Mode chip row — horizontal scrollable on mobile */}
            <div style={S.modeChipScroll} className="frame-scroll">
              <div style={S.modeChipRow}>
                {MODE_ORDER.map((id) => {
                  const mode = SCORING_MODES[id];
                  const isActive = selectedMode === id;
                  const isLocked = mode.requiresPremium && userTier === SubscriptionTier.Free;
                  const sessionLimit = entitlements.photosPerSession[id];
                  const used = sessionPhotosScored[id] || 0;
                  const atLimit = sessionLimit !== Infinity && used >= sessionLimit && userTier === SubscriptionTier.Free;
                  return (
                    <button
                      key={id}
                      className="frame-chip frame-focus"
                      onClick={() => {
                        if (isLocked) { setShowUpgrade(true); return; }
                        setSelectedMode(id);
                      }}
                      style={{
                        ...S.modeChip,
                        backgroundColor: isActive ? C.teal : isLocked ? C.surfaceHover : C.surface,
                        color: isActive ? C.white : isLocked ? C.muted : C.text,
                        fontWeight: isActive ? 600 : 500,
                        boxShadow: isActive ? '0 0 10px rgba(80,184,168,0.2), inset 0 1px 0 rgba(255,255,255,0.08)' : 'none',
                        opacity: isLocked ? 0.6 : 1,
                      }}
                      aria-pressed={isActive}
                    >
                      {mode.label}
                      {isLocked && <span style={S.lockBadge}>{Icon.lock(12, C.muted)}</span>}
                      {atLimit && !isLocked && <span style={{ ...S.lockBadge, color: C.error }}>limit</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <p style={S.tagline}>{currentMode.tagline}</p>

            {/* Divider */}
            <div style={S.sectionDivider} />

            {/* Weight distribution bars — clearly separated */}
            <h3 style={{ ...S.sectionLabel, marginTop: 0 }}>Weight Distribution</h3>
            <div style={S.weightGrid}>
              {DIMENSIONS.map((dim, idx) => {
                const pct = Math.round(modeWeights[idx] * 100);
                return (
                  <div key={dim.key} style={{ ...S.weightRow, opacity: pct === 0 ? 0.45 : 1 }}>
                    <span style={S.weightLabel}>
                      {dim.naturalLanguageLabel}
                      {pct === 0 && <span style={{ fontSize: 11, color: C.muted }}> (off)</span>}
                    </span>
                    <div style={S.weightBarTrack} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${dim.naturalLanguageLabel}: ${pct}%`}>
                      <div
                        className="frame-bar-animate"
                        style={{ ...S.weightBarFill, width: `${pct}%` }}
                      />
                    </div>
                    <span style={S.weightPct}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════════════════
            SECTION 2: VIBE CHAT BOX + PHOTO COUNT + CTA
            Clearly BELOW the mode/weights section — enlarged, no overlap
            ════════════════════════════════════════════════════ */}
        {files.length > 0 && (
          <section style={S.card} className="frame-animate-in" role="region" aria-label="Vibe description">
            <h3 style={S.sectionLabel}>Describe Your Vibe</h3>

            <textarea
              ref={vibeRef}
              className="frame-vibe frame-focus"
              value={vibeText}
              onChange={(e) => setVibeText(e.target.value)}
              placeholder={`Describe what you're looking for...\n\nExamples:\n• "Best photos from my Italy trip with good lighting"\n• "Professional headshots with clean backgrounds"\n• "Candid moments, skip group shots"\n• "Golden hour near water"\n• "Dating pics — natural smile, good light, no crowds"`}
              style={S.vibeInput}
              maxLength={500}
              rows={8}
              aria-label="Describe your photo preferences"
            />
            <p style={S.chatHint}>{currentMode.chatHint}</p>

            {/* Divider */}
            <div style={S.sectionDivider} />

            {/* Requested count */}
            <div style={S.countRow}>
              <label htmlFor="photo-count" style={S.countLabel}>I want</label>
              <input
                id="photo-count"
                type="text"
                value={countInput}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setCountInput(raw);
                  const n = parseInt(raw, 10);
                  if (!isNaN(n) && n > 0 && n <= 9999) setRequestedCount(n);
                }}
                onBlur={() => {
                  const n = parseInt(countInput, 10);
                  if (isNaN(n) || n < 1) {
                    setCountInput(String(requestedCount));
                  } else {
                    const clamped = Math.min(n, 9999);
                    setRequestedCount(clamped);
                    setCountInput(String(clamped));
                  }
                }}
                style={S.countInput}
                className="frame-focus"
                inputMode="numeric"
                onFocus={(e) => e.target.select()}
              />
              <span style={S.countLabel}>photos</span>
            </div>
            <p style={S.countHint}>
              FRAME will keep your top {requestedCount} using {currentMode.label} scoring
            </p>

            {/* Score CTA button */}
            <button
              className="frame-cta frame-focus"
              onClick={handleScore}
              disabled={scoring || files.length === 0}
              style={{
                ...S.scoreCTA,
                opacity: scoring ? 0.6 : 1,
                cursor: scoring ? 'not-allowed' : 'pointer',
              }}
              aria-busy={scoring}
            >
              {scoring
                ? `Scoring ${progress.current} of ${progress.total}...`
                : <>{Icon.star(16, C.white)}&ensp;Score My Photos</>}
            </button>

            {/* Usage counter / tier status */}
            {!authState.isAuthenticated ? (
              <p style={{ fontSize: 13, color: C.secondary, textAlign: 'center', marginTop: 8 }}>
                {UsageTracker.photosRemaining() > 0 ? (
                  <>{UsageTracker.photosRemaining()} free photos remaining</>
                ) : (
                  <>Free limit reached — <button onClick={() => { setAuthMode('signup'); setAuthError(''); setAuthSuccess(''); setShowAuthGate(true); }} style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>create a free account</button> to continue</>
                )}
              </p>
            ) : userTier === SubscriptionTier.Free ? (
              <p style={{ fontSize: 13, color: C.secondary, textAlign: 'center', marginTop: 8 }}>
                {SCORING_MODES[selectedMode].requiresPremium ? (
                  <>This mode requires <button onClick={() => setShowUpgrade(true)} style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>Silver or Gold</button></>
                ) : (
                  <>Free tier &middot; <button onClick={() => setShowUpgrade(true)} style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>Upgrade</button> for all modes + AI editing</>
                )}
              </p>
            ) : null}

            {/* Scoring error */}
            {scoringError && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '10px 14px', borderRadius: 8, marginTop: 12, fontSize: 13, textAlign: 'center' }}>
                {scoringError}
              </div>
            )}

            {/* Progress bar */}
            {scoring && (
              <div style={{ marginTop: 16 }}>
                <div style={S.progressTrack} role="progressbar" aria-valuenow={progress.current} aria-valuemax={progress.total}>
                  <div style={{
                    ...S.progressFill,
                    width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`,
                  }} />
                </div>
                <p style={S.privacyNote}>{Icon.shield(14, C.secondary)} All scoring happens on your device</p>
              </div>
            )}
          </section>
        )}

        {/* ─── Results ─── */}
        {results.length > 0 && (
          <section ref={resultsRef} style={S.card} className="frame-animate-in" role="region" aria-label="Scoring results">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ ...S.sectionLabel, marginBottom: 0 }}>
                Results — keeping top {selectedCount} of {results.length}
                {qualityFloorExcluded > 0 && (
                  <span style={{ fontSize: 12, color: C.muted, fontWeight: 400, marginLeft: 8 }}>
                    ({qualityFloorExcluded} non-photo{qualityFloorExcluded > 1 ? 's' : ''} excluded)
                  </span>
                )}
              </h3>
              <button
                onClick={() => navigate('/edit', { state: { scoredPhotos: results } })}
                style={{
                  padding: '6px 16px', borderRadius: 8, border: 'none',
                  background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`,
                  color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                Edit Photos
              </button>
            </div>

            {/* Upgrade nudge — non-blocking inline banner */}
            {showUpgradeNudge && (
              <div style={{ background: C.tealLight, borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <p style={{ color: C.text, fontSize: 13, margin: 0, lineHeight: 1.4 }}>
                  Enjoying FRAME? Unlock all 7 modes, AI editing, and more on the FRAME iOS app — <span style={{ color: C.teal, fontWeight: 600 }}>coming soon!</span>
                </p>
                <button onClick={() => setShowUpgradeNudge(false)} style={{ color: C.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0, display: 'flex', alignItems: 'center' }} aria-label="Dismiss">{Icon.close(14, C.muted)}</button>
              </div>
            )}

            {/* Score histogram */}
            <div style={S.histogram} role="img" aria-label="Score distribution chart">
              {(() => {
                const bins = new Array(10).fill(0);
                results.forEach((s) => bins[Math.min(9, Math.floor(s.compositeScore))]++);
                const maxBin = Math.max(...bins, 1);
                const threshold = results.length > selectedCount
                  ? results[selectedCount - 1]?.compositeScore ?? 0
                  : 0;
                return bins.map((count, i) => {
                  const aboveThreshold = i + 0.5 >= threshold;
                  const barColor = aboveThreshold ? scoreColor(i + 0.5) : 'rgba(100,100,100,0.3)';
                  return (
                    <div key={i} style={S.histBarWrap}>
                      {count > 0 && <span style={S.histCount}>{count}</span>}
                      <div style={{
                        ...S.histBar,
                        height: `${Math.max((count / maxBin) * 100, count > 0 ? 8 : 2)}%`,
                        backgroundColor: barColor,
                        opacity: aboveThreshold ? 1 : 0.5,
                      }} />
                      <span style={S.histLabel}>{i}</span>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Photo grid — responsive 3-col mobile, 4-col tablet, 5-col desktop */}
            <div style={S.photoGrid}>
              {results.map((photo, i) => {
                const isSelected = i < selectedCount;
                return (
                  <div
                    key={photo.id}
                    className="frame-photo frame-focus"
                    onClick={() => { setSelectedPhoto(photo); PrismTracker.startViewDetail(photo.id); }}
                    style={{
                      ...S.photoCard,
                      borderColor: isSelected ? C.teal : 'transparent',
                      boxShadow: isSelected ? '0 0 10px rgba(80,184,168,0.2)' : 'none',
                      opacity: isSelected ? 1 : 0.5,
                      cursor: 'pointer',
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedPhoto(photo); PrismTracker.startViewDetail(photo.id); } }}
                    aria-label={`Photo score ${formatScore(photo.compositeScore)}`}
                  >
                    {photo.thumbnailUri && (
                      <img src={photo.thumbnailUri} alt="" style={S.photoImg} loading="lazy" />
                    )}
                    <div style={{
                      ...S.scoreBadge,
                      backgroundColor: scoreColor(photo.compositeScore),
                    }}>
                      {formatScore(photo.compositeScore)}
                    </div>
                    {photo.photoCategory && photo.photoCategory !== 'genuine' && (
                      <div style={{
                        position: 'absolute' as const, top: 6, left: 6,
                        padding: '2px 6px', borderRadius: 4,
                        background: photo.photoCategory === 'meme' ? 'rgba(147,51,234,0.85)'
                          : photo.photoCategory === 'screenshot' ? 'rgba(100,116,139,0.85)'
                          : photo.photoCategory === 'cartoon' ? 'rgba(234,88,12,0.85)'
                          : photo.photoCategory === 'illustration' ? 'rgba(59,130,246,0.85)'
                          : photo.photoCategory === 'explicit' ? 'rgba(239,68,68,0.85)'
                          : 'rgba(59,130,246,0.85)',
                        backdropFilter: 'blur(4px)',
                        color: '#fff', fontSize: 9, fontWeight: 700, textTransform: 'capitalize' as const,
                      }}>
                        {photo.photoCategory}
                      </div>
                    )}
                    {photo.photoTags && photo.photoTags.length > 0 && !photo.photoCategory?.match(/screenshot|meme|cartoon|illustration/) && (
                      <div style={{
                        position: 'absolute' as const, bottom: 28, left: 6,
                        display: 'flex', gap: 3, flexWrap: 'wrap', maxWidth: '80%',
                      }}>
                        {photo.photoTags.slice(0, 2).map((tag: string) => (
                          <span key={tag} style={{
                            background: 'rgba(80,184,168,0.85)', color: '#fff',
                            fontSize: 9, padding: '1px 5px', borderRadius: 4, fontWeight: 600,
                          }}>{tag}</span>
                        ))}
                      </div>
                    )}
                    {photo.duplicateGroupId && (
                      <div style={{
                        position: 'absolute' as const, top: photo.photoCategory && photo.photoCategory !== 'genuine' ? 28 : 6, left: 6,
                        padding: '2px 6px', borderRadius: 4,
                        background: 'rgba(100,116,139,0.85)', backdropFilter: 'blur(4px)',
                        color: '#e2e8f0', fontSize: 9, fontWeight: 600,
                      }}>
                        Similar to {photo.dimensionNotes._duplicate?.replace('Similar to ', '') || 'another'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Score Again / New Photos button */}
            <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'center' }}>
              <button
                onClick={() => fileRef.current?.click()}
                className="frame-focus"
                style={{
                  padding: '12px 24px', borderRadius: 10,
                  border: `1px solid ${C.teal}`, background: 'transparent',
                  color: C.teal, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = C.tealLight)}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                {Icon.camera(16, C.teal)} Score New Photos
              </button>
            </div>
          </section>
        )}

        {/* ─── Score Card Modal ─── */}
        {selectedPhoto && (
          <div
            className="frame-modal-backdrop"
            style={S.modalOverlay}
            onClick={() => { if (selectedPhoto) PrismTracker.endViewDetail(selectedPhoto.id); setSelectedPhoto(null); }}
            role="dialog"
            aria-modal="true"
            aria-label="Photo score card"
          >
            <div style={S.modal} onClick={(e) => e.stopPropagation()} className="frame-scroll">
              <button
                onClick={() => { if (selectedPhoto) PrismTracker.endViewDetail(selectedPhoto.id); setSelectedPhoto(null); }}
                style={S.modalClose}
                className="frame-focus"
                aria-label="Close score card"
              >{Icon.close(16, '#fff')}</button>

              {selectedPhoto.thumbnailUri && (
                <img src={selectedPhoto.thumbnailUri} alt="" style={S.modalImg} />
              )}

              <div style={S.modalBody}>
                {/* Composite score */}
                <div style={S.modalScoreRow}>
                  {/* v2: Tier label with emoji */}
                  <span style={{
                    fontSize: 14, fontWeight: 600,
                    color: assignTier(selectedPhoto.compositeScore).color,
                    display: 'block', marginBottom: 4,
                    letterSpacing: 0.3,
                  }}>
                    {assignTier(selectedPhoto.compositeScore).emoji}{' '}
                    {assignTier(selectedPhoto.compositeScore).label}
                  </span>
                  <span style={{
                    ...S.modalComposite,
                    color: assignTier(selectedPhoto.compositeScore).color,
                  }}>
                    {formatScore(selectedPhoto.compositeScore)}
                  </span>
                  {/* v2: Glow score — optimistic ceiling */}
                  {selectedPhoto.glowScore != null &&
                   selectedPhoto.glowScore > selectedPhoto.compositeScore + 0.3 && (
                    <span style={{
                      fontSize: 28, fontWeight: 700,
                      color: assignTier(selectedPhoto.glowScore).color,
                      opacity: 0.55, display: 'inline-block', marginLeft: 6,
                      textShadow: `0 0 14px ${assignTier(selectedPhoto.glowScore).color}40`,
                    }}>
                      {'\u2192'} {formatScore(selectedPhoto.glowScore)}
                    </span>
                  )}
                  <span style={S.modalPctLabel}>
                    {selectedPhoto.glowScore != null &&
                     selectedPhoto.glowScore > selectedPhoto.compositeScore + 0.3
                      ? selectedPhoto.glowExplanation || 'Your potential with improvements'
                      : selectedPhoto.dimensionNotes?._percentile || percentileLabel(selectedPhoto.compositeScore, allComposites)}
                  </span>
                </div>

                {/* Dimension bars */}
                {DIMENSIONS.map((dim) => {
                  const score =
                    dim.key === 'portraitComposition' ? selectedPhoto.dimensions.portraitComposition :
                    dim.key === 'lighting' ? selectedPhoto.dimensions.lighting :
                    dim.key === 'composition' ? selectedPhoto.dimensions.composition :
                    selectedPhoto.dimensions.sceneInterestSpiked;
                  if (score === null) return null;

                  const barColor = scoreColor(score);
                  return (
                    <div key={dim.key} style={S.dimRow}>
                      <div style={S.dimHeader}>
                        <span style={S.dimLabel}>{dim.label}</span>
                        <span style={{ ...S.dimScore, color: barColor }}>
                          {formatScore(score)}
                        </span>
                      </div>
                      <div style={S.dimTrack}>
                        <div
                          className="frame-bar-animate"
                          style={{
                            ...S.dimFill,
                            width: `${score * 10}%`,
                            backgroundColor: barColor,
                          }}
                        />
                      </div>
                      <p style={S.dimNote}>{dim.noteForScore(score)}</p>
                    </div>
                  );
                })}

                {/* v2: Affirmation-first diagnostic card */}
                {selectedPhoto.dimensionNotes?._diagnosticV2 && (() => {
                  try {
                    const diag = JSON.parse(selectedPhoto.dimensionNotes._diagnosticV2) as {
                      strengths: string[];
                      opportunity: string;
                      estimatedImproved: number;
                    };
                    return (
                      <div style={{
                        marginTop: 16, padding: '12px 14px',
                        background: `${C.teal}08`, borderRadius: 10,
                        border: `1px solid ${C.teal}20`,
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>
                          What's working:
                        </div>
                        {diag.strengths.map((s: string, i: number) => (
                          <div key={i} style={{ fontSize: 12, color: C.secondary, marginBottom: 4 }}>
                            {'\u25cf'} {s}
                          </div>
                        ))}
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 10, marginBottom: 4 }}>
                          {'\ud83d\udca1'} Your biggest opportunity:
                        </div>
                        <div style={{ fontSize: 12, color: C.secondary }}>
                          {diag.opportunity}
                        </div>
                        {diag.estimatedImproved > selectedPhoto.compositeScore + 0.5 && (
                          <div style={{ fontSize: 12, color: C.teal, fontWeight: 600, marginTop: 6 }}>
                            Estimated with improvement: {formatScore(diag.estimatedImproved)} {assignTier(diag.estimatedImproved).emoji}
                          </div>
                        )}
                      </div>
                    );
                  } catch { return null; }
                })()}

                {/* Novel dimensions (if computed) */}
                {selectedPhoto.novelDimensions && Object.values(selectedPhoto.novelDimensions).some(v => v !== null) && (
                  <>
                    <div style={{ ...S.dimHeader, marginTop: 16, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase' as const, letterSpacing: 0.5, fontWeight: 600 }}>Advanced Analysis</span>
                      {selectedPhoto.blendedCompositeScore != null && (
                        <span style={{ fontSize: 12, color: C.teal, fontWeight: 600 }}>{formatScore(selectedPhoto.blendedCompositeScore)}</span>
                      )}
                    </div>
                    {NOVEL_DIMENSIONS.filter(nd => !nd.requiresFace || selectedPhoto.detectedFaceCount > 0)
                      .filter(nd => !nd.requiresBurst)
                      .map(nd => {
                        const score = selectedPhoto.novelDimensions?.[nd.key] ?? null;
                        if (score === null) return null;
                        const barColor = scoreColor(score);
                        return (
                          <div key={nd.key} style={S.dimRow}>
                            <div style={S.dimHeader}>
                              <span style={S.dimLabel}>{nd.shortLabel}</span>
                              <span style={{ ...S.dimScore, color: barColor }}>{formatScore(score)}</span>
                            </div>
                            <div style={S.dimTrack}>
                              <div className="frame-bar-animate" style={{ ...S.dimFill, width: `${score * 10}%`, backgroundColor: barColor }} />
                            </div>
                            <p style={S.dimNote}>{nd.noteForScore(score)}</p>
                          </div>
                        );
                      })}
                  </>
                )}

                {/* Badges */}
                {selectedPhoto.landmarkTier > 0 && (
                  <div style={S.badge}>
                    {Icon.mapPin(14, C.teal)} Landmark detected (+{formatScore(
                      selectedPhoto.dimensions.sceneInterestSpiked - selectedPhoto.dimensions.sceneInterest
                    )})
                  </div>
                )}
                {selectedPhoto.detractorPenalty > 0.05 && (
                  <div style={{ ...S.badge, ...S.detractorBadge }}>
                    {Icon.warning(14, '#ef4444')}{' '}
                    {selectedPhoto.dimensionNotes?._detractors
                      ? selectedPhoto.dimensionNotes._detractors
                      : `Quality penalty −${Math.round(selectedPhoto.detractorPenalty * 100)}%`}
                  </div>
                )}

                {/* PRISM personalization indicator */}
                {selectedPhoto.prismAdjustment && selectedPhoto.prismAdjustment.prismDelta !== 0 && (
                  <div style={{ ...S.badge, borderColor: C.tealMedium, background: C.tealLight }}>
                    {Icon.prism(14, C.teal)}{' '}
                    <span style={{ color: C.teal, fontWeight: 600 }}>Personalized</span>
                    <span style={{ color: C.secondary, marginLeft: 6, fontSize: 12 }}>
                      base: {formatScore(selectedPhoto.prismAdjustment.baseComposite)}
                      {' '}{selectedPhoto.prismAdjustment.prismDelta > 0 ? '+' : ''}{formatScore(selectedPhoto.prismAdjustment.prismDelta)} style match
                    </span>
                  </div>
                )}

                {/* Action buttons — behavioral signal collection for PRISM */}
                {/* ── Mode Suggestion Banner ── */}
                {selectedPhoto.modeSuggestion && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', marginTop: 10,
                    background: `linear-gradient(135deg, ${C.teal}15, ${C.blue}15)`,
                    border: `1px solid ${C.teal}40`,
                    borderRadius: 10,
                  }}>
                    <span style={{ fontSize: 18 }}>&#128161;</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
                        Try {selectedPhoto.modeSuggestion.suggestedMode.charAt(0).toUpperCase() + selectedPhoto.modeSuggestion.suggestedMode.slice(1)} mode
                      </div>
                      <div style={{ fontSize: 12, color: C.secondary, marginTop: 2 }}>
                        {selectedPhoto.modeSuggestion.reason}
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedMode(selectedPhoto.modeSuggestion!.suggestedMode as ScoringModeId); setSelectedPhoto(null); setPrismToast(`Switched to ${selectedPhoto.modeSuggestion!.suggestedMode} mode`); setTimeout(() => setPrismToast(null), 2000); }}
                      style={{
                        background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`,
                        color: '#fff', border: 'none', borderRadius: 8,
                        padding: '6px 12px', fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      Try it
                    </button>
                  </div>
                )}

                {/* v2: Disclaimer — FRAME scores photos, not people */}
                <p style={{
                  fontSize: 11, color: C.muted || C.secondary, textAlign: 'center' as const,
                  marginTop: 16, lineHeight: 1.4, fontStyle: 'italic' as const,
                  opacity: 0.7,
                }}>
                  FRAME scores photos, not people. You in different light = different score.
                </p>

                {/* ── Photo Tags ── */}
                {selectedPhoto.photoTags && selectedPhoto.photoTags.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                    {selectedPhoto.photoTags.map((tag: string) => (
                      <span key={tag} style={{
                        background: `${C.teal}20`, color: C.teal, fontSize: 11,
                        padding: '3px 8px', borderRadius: 6, fontWeight: 600,
                        border: `1px solid ${C.teal}30`,
                      }}>{tag}</span>
                    ))}
                  </div>
                )}

                {/* ── Action Buttons — prominent, sticky ── */}
                <div style={{
                  display: 'flex', gap: 8, marginTop: 14, justifyContent: 'center',
                  padding: '12px 0 4px', borderTop: `1px solid ${C.divider}`,
                  position: 'sticky' as const, bottom: 0, background: C.surface,
                  zIndex: 2,
                }}>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const isFav = favorites.has(selectedPhoto.id);
                      const next = new Set(favorites);
                      if (isFav) {
                        next.delete(selectedPhoto.id);
                        PrismTracker.unfavorite(selectedPhoto.id);
                        FavoritesStore.remove(selectedPhoto.id);
                      } else {
                        next.add(selectedPhoto.id);
                        PrismTracker.favorite(selectedPhoto.id);
                        let originalBlob: Blob | undefined;
                        const fileIdx = results.indexOf(selectedPhoto);
                        if (fileIdx >= 0 && files[fileIdx]) {
                          originalBlob = files[fileIdx];
                        } else if (selectedPhoto.thumbnailUri) {
                          try { const r = await fetch(selectedPhoto.thumbnailUri); originalBlob = await r.blob(); } catch {}
                        }
                        FavoritesStore.add({
                          id: selectedPhoto.id,
                          compositeScore: selectedPhoto.compositeScore,
                          dimensions: selectedPhoto.dimensions as unknown as Record<string, number>,
                          mode: selectedMode,
                          favoritedAt: new Date().toISOString(),
                          originalBlob,
                          thumbnailBlob: originalBlob,
                          fileName: files[fileIdx]?.name || `frame-${selectedPhoto.id}.jpg`,
                        });
                      }
                      setFavorites(next);
                    }}
                    style={{
                      background: favorites.has(selectedPhoto.id) ? C.teal : 'none',
                      border: `2px solid ${C.teal}`,
                      borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                      color: favorites.has(selectedPhoto.id) ? '#fff' : C.teal,
                      fontSize: 14, fontWeight: 600, minHeight: 44,
                    }}
                    aria-label={favorites.has(selectedPhoto.id) ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {Icon.heart(16, favorites.has(selectedPhoto.id) ? '#fff' : C.teal, favorites.has(selectedPhoto.id))}
                    {favorites.has(selectedPhoto.id) ? 'Saved' : 'Favorite'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (navigator.share) {
                        navigator.share({ title: `FRAME Score: ${formatScore(selectedPhoto.compositeScore)}`, url: window.location.href }).catch(() => {});
                      }
                      PrismTracker.share(selectedPhoto.id);
                      setPrismToast('Shared');
                      setTimeout(() => setPrismToast(null), 2000);
                    }}
                    style={{
                      background: 'none', border: `2px solid ${C.teal}80`,
                      borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                      color: C.teal, fontSize: 14, fontWeight: 600, minHeight: 44,
                    }}
                    aria-label="Share"
                  >
                    {Icon.shareArrow(16, C.teal)} Share
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      let blob: Blob | undefined;
                      const fileIdx = results.indexOf(selectedPhoto);
                      if (fileIdx >= 0 && files[fileIdx]) {
                        blob = files[fileIdx];
                      } else if (selectedPhoto.thumbnailUri) {
                        try { const r = await fetch(selectedPhoto.thumbnailUri); blob = await r.blob(); } catch {}
                      }
                      if (!blob) {
                        const fav = await FavoritesStore.get(selectedPhoto.id);
                        blob = fav?.originalBlob;
                      }
                      if (blob) {
                        const fileName = files[fileIdx]?.name || `frame-photo-${selectedPhoto.id.slice(0, 8)}.jpg`;
                        // iOS: use Web Share API for saving to Photos
                        try {
                          const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                          if (navigator.share && navigator.canShare?.({ files: [file] })) {
                            await navigator.share({ files: [file] });
                            setPrismToast('Saved');
                            setTimeout(() => setPrismToast(null), 2000);
                            return;
                          }
                        } catch {}
                        // Desktop/Android fallback: standard download
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = fileName;
                        a.click();
                        URL.revokeObjectURL(url);
                        setPrismToast('Downloaded');
                        setTimeout(() => setPrismToast(null), 2000);
                      }
                    }}
                    style={{
                      background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`,
                      border: 'none', borderRadius: 10, padding: '10px 16px',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                      color: '#fff', fontSize: 14, fontWeight: 600, minHeight: 44,
                    }}
                    aria-label="Download photo"
                  >
                    {Icon.download(16, '#fff')} Download
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      PrismTracker.disagree(selectedPhoto.id);
                      setPrismToast('Feedback recorded');
                      setTimeout(() => setPrismToast(null), 2000);
                    }}
                    style={{
                      background: 'none', border: `2px solid ${C.divider}`,
                      borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                      color: C.secondary, fontSize: 14, fontWeight: 500, minHeight: 44,
                    }}
                    aria-label="Disagree with score"
                    title="This score doesn't match my opinion"
                  >
                    {Icon.thumbsDown(16, C.secondary)} Disagree
                  </button>
                </div>

                {/* Toast notification */}
                {prismToast && (
                  <div style={{
                    textAlign: 'center', marginTop: 10, fontSize: 12, color: C.teal,
                    padding: '6px 14px', background: C.tealLight, borderRadius: 8,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    margin: '10px auto 0', animation: 'frameFadeIn 0.2s ease',
                  }}>
                    {Icon.star(12, C.teal)} {prismToast}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── PRISM Enrollment Banner ─── */}
        {results.length > 0 && !prismEnabled && !enrollmentDismissed && results.some(r => r.detectedFaceCount > 0 && r.prismAesthetics?.isSelfie) && (
          <section style={{ margin: '24px auto', maxWidth: 540, padding: '16px 20px', background: 'rgba(80,184,168,0.06)', border: `1px solid rgba(80,184,168,0.2)`, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
              {Icon.prism(18, C.teal)}
              <span style={{ color: C.text, fontSize: 15, fontWeight: 600 }}>Adaptive Scoring</span>
            </div>
            <p style={{ color: C.secondary, fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
              FRAME can learn your preferences over time to personalize scores.
              All data stays on your device — no uploads, no tracking.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => { PrismStore.setEnabled(true); setPrismEnabled(true); }}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`, color: C.white, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >Enable</button>
              <button
                onClick={() => setEnrollmentDismissed(true)}
                style={{ padding: '8px 20px', borderRadius: 8, border: `1px solid ${C.divider}`, background: 'none', color: C.secondary, fontSize: 13, cursor: 'pointer' }}
              >Not Now</button>
            </div>
          </section>
        )}

        {/* ─── App Download CTA ─── */}
        <section style={S.ctaSection}>
          <p style={S.ctaText}>
            The full FRAME experience — all modes, AI editing, and adaptive scoring
          </p>
          <p style={S.ctaSubtext}>Coming soon to iOS and Android</p>
        </section>

        {/* ─── Footer ─── */}
        <footer style={S.footer}>
          <a href="https://www.sandstonecloud.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginBottom: 10, opacity: 0.7, transition: 'opacity 0.2s' }} onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0.7')}>
            <img src="/sandstone-logo.png" alt="Sandstone Cloud" style={{ height: 40, width: 'auto' }} />
          </a>
          <br />
          FRAME — Photo Companion by Sandstone Cloud
          {prismEnabled && (
            <div style={{ marginTop: 8, fontSize: 11, color: C.muted, lineHeight: 1.4 }}>
              Adaptive Scoring processes quality signals and interaction patterns on-device.
              Only scores and preferences are stored locally. No facial data is uploaded or retained.
              <button onClick={() => setShowPrismSettings(true)} style={{ background: 'none', border: 'none', color: C.teal, fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0, marginLeft: 4 }}>Manage</button>
            </div>
          )}
        </footer>
      </main>

      {/* ─── PRISM Settings Modal ─── */}
      {showPrismSettings && (() => {
        const pv = PrismStore.getPreferenceVector();
        const enrollment = PrismStore.getEnrollment();
        const localIsPaused = prismPaused;
        const confidenceLabel = getConfidenceLabel(pv.confidence);
        const confidencePct = Math.round(pv.confidence * 100);
        const styleDimensions = [
          { label: 'Warmth', value: pv.warmthPreference, descriptor: pv.warmthPreference > 0.2 ? 'warm' : pv.warmthPreference < -0.2 ? 'cool' : 'neutral' },
          { label: 'Contrast', value: pv.contrastPreference, descriptor: pv.contrastPreference > 0.2 ? 'punchy' : pv.contrastPreference < -0.2 ? 'soft' : 'moderate' },
          { label: 'Saturation', value: pv.saturationPreference, descriptor: pv.saturationPreference > 0.2 ? 'vibrant' : pv.saturationPreference < -0.2 ? 'muted' : 'balanced' },
          { label: 'Backgrounds', value: pv.backgroundPreference, descriptor: pv.backgroundPreference > 0.2 ? 'clean' : pv.backgroundPreference < -0.2 ? 'detailed' : 'varied' },
          { label: 'Crop', value: pv.cropPreference, descriptor: pv.cropPreference > 0.2 ? 'tight' : pv.cropPreference < -0.2 ? 'wide' : 'balanced' },
        ];
        // Photo stats from current results + localStorage
        const totalPhotosScored = results.length;
        const avgScore = totalPhotosScored > 0 ? results.reduce((sum, r) => sum + r.compositeScore, 0) / totalPhotosScored : 0;
        const topScore = totalPhotosScored > 0 ? Math.max(...results.map(r => r.compositeScore)) : 0;
        const tierLabel = userTier === SubscriptionTier.Gold ? 'Gold' : userTier === SubscriptionTier.Silver ? 'Silver' : 'Free';
        const tierColor = userTier === SubscriptionTier.Gold ? C.teal : userTier === SubscriptionTier.Silver ? '#94a3b8' : C.muted;
        const chatSessions = ChatService.getSessions(authState.user?.userId || '');
        const settingSection = { marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${C.divider}` };
        const settingLabel = { color: C.text, fontSize: 14, fontWeight: 600 as const, marginBottom: 10 };
        const settingRow = { display: 'flex' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, marginBottom: 6 };
        const settingMuted = { color: C.secondary, fontSize: 12 };
        const settingValue = { color: C.text, fontSize: 13, fontWeight: 500 as const };
        return (
          <div style={S.modalOverlay} onClick={() => setShowPrismSettings(false)} role="dialog" aria-modal="true" aria-label="Settings">
            <div style={{ ...S.modalCard, maxWidth: 440, padding: 0, maxHeight: '85vh', display: 'flex', flexDirection: 'column' as const }} onClick={e => e.stopPropagation()}>
              {/* Settings header */}
              <div style={{ padding: '18px 24px 14px', borderBottom: `1px solid ${C.divider}`, flexShrink: 0 }}>
                <button onClick={() => setShowPrismSettings(false)} style={{ ...S.modalClose, top: 16, right: 16 }} aria-label="Close settings">
                  {Icon.close(16, '#fff')}
                </button>
                <h3 style={{ color: C.text, fontSize: 20, fontWeight: 700, margin: 0 }}>Settings</h3>
              </div>

              <div style={{ padding: '20px 24px', overflowY: 'auto' as const, flex: 1 }}>

                {/* ── Account ── */}
                <div style={settingSection}>
                  <div style={settingLabel}>Account</div>
                  <div style={settingRow}>
                    <span style={settingMuted}>Name</span>
                    <span style={settingValue}>{displayName || authState.user?.email?.split('@')[0] || '—'}</span>
                  </div>
                  <div style={settingRow}>
                    <span style={settingMuted}>Email</span>
                    <span style={{ ...settingValue, fontSize: 12 }}>{authState.user?.email || '—'}</span>
                  </div>
                  <div style={settingRow}>
                    <span style={settingMuted}>Plan</span>
                    <span style={{ ...settingValue, color: tierColor, fontWeight: 700 }}>{tierLabel}</span>
                  </div>
                  {userTier === SubscriptionTier.Free && (
                    <button onClick={() => { setShowPrismSettings(false); setShowUpgrade(true); }}
                      style={{ marginTop: 6, width: '100%', padding: '8px 0', borderRadius: 8, border: 'none', background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`, color: C.white, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      Upgrade to unlock all modes + PRISM
                    </button>
                  )}
                </div>

                {/* ── Photo Stats ── */}
                <div style={settingSection}>
                  <div style={settingLabel}>Photo Stats</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
                    <div style={{ textAlign: 'center' as const, padding: '10px 0', background: 'rgba(80,184,168,0.06)', borderRadius: 8 }}>
                      <div style={{ color: C.teal, fontSize: 22, fontWeight: 700 }}>{totalPhotosScored}</div>
                      <div style={{ color: C.muted, fontSize: 10 }}>Photos Scored</div>
                    </div>
                    <div style={{ textAlign: 'center' as const, padding: '10px 0', background: 'rgba(80,184,168,0.06)', borderRadius: 8 }}>
                      <div style={{ color: C.teal, fontSize: 22, fontWeight: 700 }}>{avgScore > 0 ? avgScore.toFixed(1) : '—'}</div>
                      <div style={{ color: C.muted, fontSize: 10 }}>Avg Score</div>
                    </div>
                    <div style={{ textAlign: 'center' as const, padding: '10px 0', background: 'rgba(80,184,168,0.06)', borderRadius: 8 }}>
                      <div style={{ color: C.teal, fontSize: 22, fontWeight: 700 }}>{topScore > 0 ? topScore.toFixed(1) : '—'}</div>
                      <div style={{ color: C.muted, fontSize: 10 }}>Best Score</div>
                    </div>
                  </div>
                  <div style={settingRow}>
                    <span style={settingMuted}>Current mode</span>
                    <span style={settingValue}>{selectedMode}</span>
                  </div>
                  <div style={settingRow}>
                    <span style={settingMuted}>Chat sessions</span>
                    <span style={settingValue}>{chatSessions.length}</span>
                  </div>
                  <div style={settingRow}>
                    <span style={settingMuted}>PRISM photos sampled</span>
                    <span style={settingValue}>{pv.photosSampled}</span>
                  </div>
                </div>

                {/* ── PRISM / Adaptive Scoring ── */}
                <div style={settingSection}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    {Icon.prism(18, C.teal)}
                    <div style={settingLabel}>PRISM Adaptive Scoring</div>
                  </div>
                  {/* Toggle */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>Personalized scoring</div>
                      <div style={{ color: C.secondary, fontSize: 11 }}>PRISM learns your visual aesthetic</div>
                    </div>
                    <button
                      onClick={() => { const next = !prismEnabled; PrismStore.setEnabled(next); setPrismEnabled(next); if (TIER_ENTITLEMENTS[userTier].prism.engramSync) { EngramService.setEnabled(next); } }}
                      style={{
                        width: 48, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                        background: prismEnabled ? C.teal : C.divider,
                        position: 'relative' as const, transition: 'background 0.2s',
                      }}
                      aria-label={prismEnabled ? 'Disable' : 'Enable'}
                    >
                      <div style={{
                        width: 22, height: 22, borderRadius: 11, background: '#fff',
                        position: 'absolute' as const, top: 3,
                        left: prismEnabled ? 23 : 3, transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      }} />
                    </button>
                  </div>

                  {prismEnabled && (
                    <>
                      {/* Confidence */}
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: C.secondary, fontSize: 11 }}>Confidence: {confidenceLabel}</span>
                          <span style={{ color: C.teal, fontSize: 11, fontWeight: 600 }}>{confidencePct}%</span>
                        </div>
                        <div style={{ height: 4, background: C.divider, borderRadius: 2 }}>
                          <div style={{ height: 4, borderRadius: 2, background: `linear-gradient(90deg, ${C.blue}, ${C.teal})`, width: `${confidencePct}%`, transition: 'width 0.3s' }} />
                        </div>
                        <div style={{ color: C.muted, fontSize: 10, marginTop: 3 }}>
                          {pv.photosSampled} photos across {enrollment.selfieSessionCount} sessions
                        </div>
                      </div>

                      {/* Style DNA */}
                      {pv.photosSampled > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Your Style DNA</div>
                          {styleDimensions.map(dim => {
                            const barWidth = Math.abs(dim.value) * 50;
                            const isPositive = dim.value >= 0;
                            return (
                              <div key={dim.label} style={{ marginBottom: 6 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                  <span style={{ color: C.secondary, fontSize: 11 }}>{dim.label}</span>
                                  <span style={{ color: C.text, fontSize: 11, fontWeight: 500 }}>{dim.descriptor}</span>
                                </div>
                                <div style={{ height: 4, background: C.divider, borderRadius: 2, position: 'relative' as const }}>
                                  <div style={{
                                    position: 'absolute' as const, top: 0, height: 4, borderRadius: 2,
                                    background: C.teal,
                                    left: isPositive ? '50%' : `${50 - barWidth}%`,
                                    width: `${barWidth}%`, transition: 'all 0.3s',
                                  }} />
                                  <div style={{ position: 'absolute' as const, left: '50%', top: -1, width: 1, height: 6, background: C.muted }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* PRISM actions */}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => { PrismStore.setPaused(!localIsPaused); setPrismPaused(!localIsPaused); }}
                          style={{ flex: 1, padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.divider}`, background: 'none', color: C.secondary, fontSize: 11, cursor: 'pointer' }}>
                          {localIsPaused ? 'Resume Learning' : 'Pause Learning'}
                        </button>
                        <button onClick={() => { if (confirm('Reset all adaptive scoring data?')) { PrismStore.reset(); setPrismEnabled(false); if (TIER_ENTITLEMENTS[userTier].prism.engramSync) { EngramService.setEnabled(false); } setShowPrismSettings(false); } }}
                          style={{ flex: 1, padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'none', color: 'rgba(239,68,68,0.7)', fontSize: 11, cursor: 'pointer' }}>
                          Reset Preferences
                        </button>
                      </div>

                      {/* ENGRAM status — auto-enabled with PRISM on Silver+ */}
                      {TIER_ENTITLEMENTS[userTier].prism.engramSync && (
                        <div style={{ marginTop: 8, fontSize: 10, color: C.success, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.success, display: 'inline-block' }} /> ENGRAM memory active
                        </div>
                      )}
                      {!TIER_ENTITLEMENTS[userTier].prism.engramSync && (
                        <div style={{ marginTop: 8, fontSize: 10, color: C.muted }}>
                          Upgrade to Silver+ to sync PRISM preferences across sessions via ENGRAM
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* ── Data & Privacy ── */}
                <div style={settingSection}>
                  <div style={settingLabel}>Data & Privacy</div>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                    <button
                      onClick={() => {
                        const exportData = {
                          exportedAt: new Date().toISOString(),
                          account: { email: authState.user?.email, displayName, tier: tierLabel },
                          prism: PrismStore.export(),
                          photoStats: { totalScored: totalPhotosScored, avgScore: avgScore.toFixed(2), topScore: topScore.toFixed(2) },
                          scoredPhotos: results.map(r => ({
                            id: r.id, composite: r.compositeScore, dimensions: r.dimensions,
                            detractorPenalty: r.detractorPenalty, mode: selectedMode,
                          })),
                          chatSessions: chatSessions.map(s => ({ id: s.sessionId, title: s.title, messageCount: s.messageCount, created: s.createdAt })),
                        };
                        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url; a.download = `frame-data-${new Date().toISOString().split('T')[0]}.json`; a.click();
                        URL.revokeObjectURL(url);
                      }}
                      style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.divider}`, background: 'none', color: C.text, fontSize: 12, cursor: 'pointer', textAlign: 'left' as const }}>
                      Download All My Data
                      <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>Photos, scores, PRISM profile, chat history</div>
                    </button>
                    <button onClick={() => { if (confirm('Clear all scoring history? Photos will need to be re-scored.')) { setResults([]); } }}
                      style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.divider}`, background: 'none', color: C.secondary, fontSize: 12, cursor: 'pointer', textAlign: 'left' as const }}>
                      Clear Scoring History
                    </button>
                    <button onClick={() => { if (confirm('Clear all chat conversations?')) { chatSessions.forEach(s => ChatService.deleteSession(s.sessionId, authState.user?.userId || '')); setShowPrismSettings(false); } }}
                      style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.divider}`, background: 'none', color: C.secondary, fontSize: 12, cursor: 'pointer', textAlign: 'left' as const }}>
                      Clear Chat History
                      <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>{chatSessions.length} conversation{chatSessions.length !== 1 ? 's' : ''}</div>
                    </button>
                    <button onClick={() => { if (confirm('Delete all PRISM data? This cannot be undone.')) { PrismStore.reset(); setPrismEnabled(false); setShowPrismSettings(false); } }}
                      style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)', background: 'none', color: 'rgba(239,68,68,0.7)', fontSize: 12, cursor: 'pointer', textAlign: 'left' as const }}>
                      Delete PRISM Data
                    </button>
                  </div>
                </div>

                {/* ── Privacy note ── */}
                <div style={{ marginBottom: 20, padding: '10px 12px', background: 'rgba(80,184,168,0.06)', borderRadius: 8, border: '1px solid rgba(80,184,168,0.15)' }}>
                  <div style={{ color: C.teal, fontSize: 10, lineHeight: 1.5 }}>
                    {prismEnabled && TIER_ENTITLEMENTS[userTier].prism.engramSync
                      ? 'PRISM syncs your preferences via ENGRAM for cross-session personalization. Only behavioral signals and style preferences are shared — never facial data, photos, or embeddings.'
                      : 'All preference data is stored on your device only. FRAME never uploads facial data, stores face templates, or shares your preferences with third parties.'}
                  </div>
                </div>

                {/* ── App Info ── */}
                <div style={{ marginBottom: 8 }}>
                  <div style={settingLabel}>About</div>
                  <div style={settingRow}>
                    <span style={settingMuted}>Version</span>
                    <span style={{ color: C.muted, fontSize: 11, fontFamily: 'monospace' }}>v{(window as any).__APP_VERSION__ || '?'} {(window as any).__COMMIT_HASH__ || ''}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                    <a href="https://sandstonecloud.com/terms" target="_blank" rel="noopener" style={{ color: C.secondary, fontSize: 11, textDecoration: 'none' }}>Terms</a>
                    <a href="https://sandstonecloud.com/privacy" target="_blank" rel="noopener" style={{ color: C.secondary, fontSize: 11, textDecoration: 'none' }}>Privacy</a>
                    <a href="https://github.com/jemell-sandstonecloud/frame/issues" target="_blank" rel="noopener" style={{ color: C.secondary, fontSize: 11, textDecoration: 'none' }}>Report a Bug</a>
                  </div>
                  <div style={{ color: C.muted, fontSize: 10, marginTop: 10 }}>Sandstone Cloud</div>
                </div>

              </div>
            </div>
          </div>
        );
      })()}

      {/* ─── My Favorites Gallery ─── */}
      {showFavorites && (
        <div style={S.modalOverlay} onClick={() => { setShowFavorites(false); Object.values(favThumbnails).forEach(URL.revokeObjectURL); setFavThumbnails({}); }}>
          <div style={{ ...S.modalCard, maxWidth: 560, padding: 0, maxHeight: '85vh', display: 'flex', flexDirection: 'column' as const }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 24px 14px', borderBottom: `1px solid ${C.divider}`, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: C.text, fontSize: 20, fontWeight: 700, margin: 0 }}>My Favorites</h3>
                <p style={{ color: C.secondary, fontSize: 12, margin: '4px 0 0' }}>{favPhotos.length} photo{favPhotos.length !== 1 ? 's' : ''} saved</p>
              </div>
              <button onClick={() => { setShowFavorites(false); Object.values(favThumbnails).forEach(URL.revokeObjectURL); setFavThumbnails({}); }} style={{ ...S.modalClose, top: 16, right: 16 }} aria-label="Close">
                {Icon.close(16, '#fff')}
              </button>
            </div>
            <div style={{ padding: '16px 24px', overflowY: 'auto' as const, flex: 1 }}>
              {favPhotos.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: C.secondary }}>
                  <div style={{ marginBottom: 8 }}>{Icon.heart(32, C.divider)}</div>
                  <p style={{ fontSize: 14, margin: '0 0 4px' }}>No favorites yet</p>
                  <p style={{ fontSize: 12, color: C.muted }}>Tap the heart on any scored photo to save it here</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                  {favPhotos.map(fav => (
                    <div key={fav.id} style={{ position: 'relative' as const, borderRadius: 10, overflow: 'hidden', background: C.surface, border: `1px solid ${C.divider}` }}>
                      {favThumbnails[fav.id] ? (
                        <img src={favThumbnails[fav.id]} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                      ) : (
                        <div style={{ width: '100%', aspectRatio: '1', background: C.divider, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 11 }}>No preview</div>
                      )}
                      <div style={{ position: 'absolute' as const, top: 6, right: 6, padding: '2px 6px', borderRadius: 6, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                        {formatScore(fav.compositeScore)}
                      </div>
                      <div style={{ padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: C.muted, fontSize: 10 }}>{fav.mode}</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={async () => {
                              if (fav.originalBlob) {
                                const url = URL.createObjectURL(fav.originalBlob);
                                const a = document.createElement('a');
                                a.href = url; a.download = fav.fileName || `frame-${fav.id.slice(0, 8)}.jpg`; a.click();
                                URL.revokeObjectURL(url);
                              }
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                            title="Download original"
                          >
                            {Icon.download(14, fav.originalBlob ? C.teal : C.divider)}
                          </button>
                          <button
                            onClick={async () => {
                              await FavoritesStore.remove(fav.id);
                              const next = new Set(favorites);
                              next.delete(fav.id);
                              setFavorites(next);
                              setFavPhotos(prev => prev.filter(p => p.id !== fav.id));
                              if (favThumbnails[fav.id]) URL.revokeObjectURL(favThumbnails[fav.id]);
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                            title="Remove from favorites"
                          >
                            {Icon.close(12, C.muted)}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Auth Gate Modal ─── */}
      {showAuthGate && (
        <div style={S.modalOverlay} onClick={() => { setShowAuthGate(false); setAuthPassword(''); setAuthNewPassword(''); setAuthCode(''); setAuthError(''); setAuthSuccess(''); setAuthMode('signup'); }}>
          <div style={{ ...S.modalCard, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: C.text, fontSize: 20, marginBottom: 4 }}>
              {authMode === 'signup' ? 'Create Your Account'
               : authMode === 'login' ? 'Welcome Back'
               : authMode === 'verify' ? 'Check Your Email'
               : authMode === 'forgot' ? 'Reset Password'
               : authMode === 'forgot-verify' ? 'Set New Password'
               : authMode === 'profile' ? 'Almost Done!' : 'Welcome'}
            </h3>
            <p style={{ color: C.secondary, fontSize: 14, marginBottom: 20 }}>
              {authMode === 'signup' ? 'Free forever — unlock all modes and save your scoring history'
               : authMode === 'login' ? 'Sign in to pick up where you left off'
               : authMode === 'verify' ? `We sent a 6-digit code to ${authEmail}`
               : authMode === 'forgot' ? 'Enter your email and we\'ll send a reset code'
               : authMode === 'forgot-verify' ? `Enter the code sent to ${authEmail} and your new password`
               : authMode === 'profile' ? 'One last thing — tell us your name'
               : ''}
            </p>

            {authSuccess && <p style={{ color: C.success, fontSize: 13, marginBottom: 12 }}>{authSuccess}</p>}
            {authError && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{authError}</p>}

            {(authMode === 'signup' || authMode === 'login') && (
              <>
                <input type="email" placeholder="Email" value={authEmail} onChange={e => setAuthEmail(e.target.value)}
                  autoComplete="email" autoFocus
                  style={{ ...S.countInput, width: '100%', marginBottom: 12, padding: '10px 12px', fontSize: 15 }} />
                <input type="password" placeholder="Password (8+ characters)" value={authPassword} onChange={e => setAuthPassword(e.target.value)}
                  autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                  onKeyDown={e => { if (e.key === 'Enter' && authEmail && authPassword && !authLoading) (e.target as HTMLElement).closest('div')?.querySelector('button')?.click(); }}
                  style={{ ...S.countInput, width: '100%', marginBottom: 16, padding: '10px 12px', fontSize: 15 }} />
                <button
                  disabled={authLoading || !authEmail || !authPassword}
                  onClick={async () => {
                    setAuthLoading(true); setAuthError(''); setAuthSuccess('');
                    try {
                      if (authMode === 'signup') {
                        await WebAuthService.signUp(authEmail, authPassword);
                        setAuthMode('verify');
                      } else {
                        await WebAuthService.signIn(authEmail, authPassword);
                        const user = await WebAuthService.getCurrentUser();
                        if (user) {
                          setAuthState({ isAuthenticated: true, user: { userId: user.userId, email: authEmail }, isLoading: false });
                          const admin = await checkAdminGroup();
                          if (admin) { setIsAdmin(true); setUserTier(SubscriptionTier.Gold); }
                          // Load display name
                          const name = await WebAuthService.getDisplayName();
                          if (name) setDisplayName(name);
                          // Create new chat session on login
                          const newSession = ChatService.createSession(user.userId);
                          setActiveSessionId(newSession.sessionId);
                          setChatSessions(ChatService.getSessions(user.userId));
                          setAuthPassword('');
                          setShowAuthGate(false);
                        } else {
                          setAuthError('Login succeeded but session failed. Please try again.');
                        }
                      }
                    } catch (err: any) {
                      if (err?.name === 'UserNotConfirmedException') setAuthMode('verify');
                      else setAuthError(err?.message || 'Something went wrong');
                    } finally { setAuthLoading(false); }
                  }}
                  style={{ ...S.scoreCTA, width: '100%', opacity: authLoading ? 0.6 : 1 }}
                >
                  {authLoading ? 'Please wait...' : authMode === 'signup' ? 'Create Account' : 'Log In'}
                </button>
                <p style={{ color: C.secondary, fontSize: 13, textAlign: 'center', marginTop: 12 }}>
                  {authMode === 'signup' ? (
                    <>Already have an account? <button onClick={() => { setAuthMode('login'); setAuthError(''); setAuthSuccess(''); }} style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>Log in</button></>
                  ) : (
                    <>
                      Need an account? <button onClick={() => { setAuthMode('signup'); setAuthError(''); setAuthSuccess(''); }} style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>Sign up</button>
                      <br />
                      <button onClick={() => { setAuthMode('forgot'); setAuthError(''); setAuthSuccess(''); setAuthPassword(''); }} style={{ color: C.muted, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', marginTop: 4 }}>Forgot password?</button>
                    </>
                  )}
                </p>
              </>
            )}

            {authMode === 'forgot' && (
              <>
                <input type="email" placeholder="Email" value={authEmail} onChange={e => setAuthEmail(e.target.value)}
                  style={{ ...S.countInput, width: '100%', marginBottom: 16, padding: '10px 12px', fontSize: 15 }} />
                <button
                  disabled={authLoading || !authEmail}
                  onClick={async () => {
                    setAuthLoading(true); setAuthError(''); setAuthSuccess('');
                    try {
                      await WebAuthService.resetPassword(authEmail);
                      setAuthMode('forgot-verify');
                    } catch (err: any) {
                      setAuthError(err?.message || 'Failed to send reset code');
                    } finally { setAuthLoading(false); }
                  }}
                  style={{ ...S.scoreCTA, width: '100%', opacity: authLoading ? 0.6 : 1 }}
                >
                  {authLoading ? 'Sending...' : 'Send Reset Code'}
                </button>
                <p style={{ color: C.secondary, fontSize: 13, textAlign: 'center', marginTop: 12 }}>
                  <button onClick={() => { setAuthMode('login'); setAuthError(''); setAuthSuccess(''); }} style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>Back to login</button>
                </p>
              </>
            )}

            {authMode === 'verify' && (
              <>
                <input type="text" placeholder="6-digit code" value={authCode} onChange={e => setAuthCode(e.target.value)} maxLength={6}
                  autoComplete="one-time-code" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && authCode.length >= 6 && !authLoading) (e.target as HTMLElement).closest('div')?.querySelector('button')?.click(); }}
                  style={{ ...S.countInput, width: '100%', marginBottom: 16, padding: '10px 12px', fontSize: 18, textAlign: 'center', letterSpacing: 8 }} inputMode="numeric" />
                <button
                  disabled={authLoading || authCode.length < 6}
                  onClick={async () => {
                    setAuthLoading(true); setAuthError(''); setAuthSuccess('');
                    try {
                      await WebAuthService.confirmSignUp(authEmail, authCode);
                      await WebAuthService.signIn(authEmail, authPassword);
                      const user = await WebAuthService.getCurrentUser();
                      if (user) {
                        setAuthState({ isAuthenticated: true, user: { userId: user.userId, email: authEmail }, isLoading: false });
                        const admin = await checkAdminGroup();
                        if (admin) { setIsAdmin(true); setUserTier(SubscriptionTier.Gold); }
                      }
                      setAuthPassword('');
                      setAuthCode('');
                      setAuthMode('profile');
                    } catch (err: any) {
                      setAuthError(err?.message || 'Verification failed');
                    } finally { setAuthLoading(false); }
                  }}
                  style={{ ...S.scoreCTA, width: '100%', opacity: authLoading ? 0.6 : 1 }}
                >
                  {authLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>
                <p style={{ color: C.secondary, fontSize: 13, textAlign: 'center', marginTop: 12 }}>
                  Didn't receive the code?{' '}
                  <button onClick={async () => {
                    try {
                      await WebAuthService.signUp(authEmail, authPassword);
                      setAuthError(''); setAuthSuccess('');
                      setAuthSuccess('New code sent!');
                    } catch (err: any) {
                      setAuthError(err?.message || 'Failed to resend code');
                    }
                  }} style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>
                    Resend code
                  </button>
                </p>
              </>
            )}

            {authMode === 'forgot-verify' && (
              <>
                <input type="text" placeholder="6-digit code" value={authCode} onChange={e => setAuthCode(e.target.value)} maxLength={6}
                  autoComplete="one-time-code" autoFocus
                  style={{ ...S.countInput, width: '100%', marginBottom: 12, padding: '10px 12px', fontSize: 18, textAlign: 'center', letterSpacing: 8 }} inputMode="numeric" />
                <input type="password" placeholder="New password (8+ characters)" value={authNewPassword} onChange={e => setAuthNewPassword(e.target.value)}
                  style={{ ...S.countInput, width: '100%', marginBottom: 16, padding: '10px 12px', fontSize: 15 }} />
                <button
                  disabled={authLoading || authCode.length < 6 || !authNewPassword}
                  onClick={async () => {
                    setAuthLoading(true); setAuthError(''); setAuthSuccess('');
                    try {
                      await WebAuthService.confirmResetPassword(authEmail, authCode, authNewPassword);
                      setAuthNewPassword('');
                      setAuthCode('');
                      setAuthMode('login');
                      setAuthError(''); setAuthSuccess('');
                      setAuthSuccess('Password reset successfully! Log in with your new password.');
                    } catch (err: any) {
                      setAuthError(err?.message || 'Password reset failed');
                    } finally { setAuthLoading(false); }
                  }}
                  style={{ ...S.scoreCTA, width: '100%', opacity: authLoading ? 0.6 : 1 }}
                >
                  {authLoading ? 'Resetting...' : 'Reset Password & Log In'}
                </button>
                <p style={{ color: C.secondary, fontSize: 13, textAlign: 'center', marginTop: 12 }}>
                  <button onClick={() => { setAuthMode('forgot'); setAuthError(''); setAuthSuccess(''); setAuthCode(''); setAuthNewPassword(''); }} style={{ color: C.teal, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>Resend code</button>
                </p>
              </>
            )}

            {authMode === 'profile' && (
              <>
                <p style={{ color: C.text, fontSize: 15, fontWeight: 600, textAlign: 'center', marginBottom: 4 }}>Set Your Display Name</p>
                <p style={{ color: C.secondary, fontSize: 13, textAlign: 'center', marginBottom: 16 }}>How should we greet you?</p>
                <input
                  type="text" placeholder="Your name" value={profileInput}
                  onChange={e => setProfileInput(e.target.value)} maxLength={30} autoFocus
                  style={{ ...S.countInput, width: '100%', marginBottom: 16, padding: '10px 12px', fontSize: 16, textAlign: 'left' as const }}
                />
                <button
                  disabled={authLoading}
                  onClick={async () => {
                    if (profileInput.trim()) {
                      setAuthLoading(true);
                      try {
                        await WebAuthService.setDisplayName(profileInput.trim());
                        setDisplayName(profileInput.trim());
                      } catch (err) {
                        console.error('[FRAME] Profile update error:', err);
                      } finally { setAuthLoading(false); }
                    }
                    // Create new chat session after signup flow
                    if (authState.user) {
                      const newSession = ChatService.createSession(authState.user.userId);
                      setActiveSessionId(newSession.sessionId);
                      setChatSessions(ChatService.getSessions(authState.user.userId));
                    }
                    setShowAuthGate(false);
                    setProfileInput('');
                  }}
                  style={{ ...S.scoreCTA, width: '100%', opacity: authLoading ? 0.6 : 1 }}
                >
                  {profileInput.trim() ? 'Continue' : 'Skip'}
                </button>
              </>
            )}

            {authMode !== 'profile' && (
              <button onClick={() => { setShowAuthGate(false); setAuthPassword(''); setAuthNewPassword(''); setAuthCode(''); setAuthError(''); setAuthSuccess(''); setAuthMode('signup'); }} style={{ color: C.muted, background: 'none', border: 'none', cursor: 'pointer', marginTop: 16, fontSize: 13, display: 'block', width: '100%', textAlign: 'center' }}>
                Continue as guest
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── Upgrade Modal ─── */}
      {showUpgrade && (
        <div style={S.modalOverlay} onClick={() => setShowUpgrade(false)}>
          <div style={{ ...S.modalCard, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowUpgrade(false)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 4, display: 'flex', alignItems: 'center' }} aria-label="Close">{Icon.close(18, C.muted)}</button>
            <h3 style={{ color: C.text, fontSize: 20, marginBottom: 16 }}>Upgrade FRAME</h3>

            <div style={{ background: 'linear-gradient(145deg, #1e293b 0%, #222d3d 100%)', borderRadius: 12, padding: 16, marginBottom: 12, border: '1px solid rgba(148,163,184,0.2)', position: 'relative', boxShadow: '0 0 12px rgba(148,163,184,0.08), inset 0 1px 0 rgba(255,255,255,0.03)' }}>
              <span style={{ position: 'absolute', top: 8, right: 8, background: '#94a3b8', color: C.bg, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', boxShadow: '0 1px 6px rgba(0,0,0,0.3)' }}>Coming Soon</span>
              <h4 style={{ color: '#c0cfe0', fontSize: 16, marginBottom: 4 }}>Silver — $4.99/mo</h4>
              <p style={{ color: C.secondary, fontSize: 13, lineHeight: 1.5 }}>250 photos/month &middot; All 7 modes &middot; Full vibe engine &middot; 20 clean downloads</p>
              <p style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>Annual: $39.99/year (save 33%)</p>
            </div>

            <div style={{ background: 'linear-gradient(145deg, #1e293b 0%, #0c1220 100%)', borderRadius: 12, padding: 16, marginBottom: 16, border: '1px solid rgba(80,184,168,0.4)', position: 'relative', boxShadow: '0 0 20px rgba(80,184,168,0.15), inset 0 1px 0 rgba(80,184,168,0.08)' }}>
              <span style={{ position: 'absolute', top: 8, right: 8, background: C.teal, color: C.bg, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', boxShadow: '0 1px 6px rgba(0,0,0,0.3)' }}>Coming Soon</span>
              <h4 style={{ color: C.teal, fontSize: 16, marginBottom: 4 }}>Gold — $9.99/mo</h4>
              <p style={{ color: C.secondary, fontSize: 13, lineHeight: 1.5 }}>Unlimited photos &middot; AI editing &middot; Compositing &middot; Social calibration</p>
              <p style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>Annual: $79.99/year (save 33%)</p>
            </div>

            <p style={{ color: C.muted, fontSize: 12, textAlign: 'center', marginBottom: 16 }}>Subscriptions available soon via the FRAME iOS app</p>

            <button onClick={() => setShowUpgrade(false)} style={{ ...S.scoreCTA, width: '100%' }}>
              Got it
            </button>
          </div>
        </div>
      )}
      {/* ── Inactivity warning toast ── */}
      {inactivityWarning && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#f59e0b', color: '#0f172a', padding: '10px 20px',
          borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 2000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)', animation: 'frameSlideUp 0.3s ease-out',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          Session expires in 5 minutes due to inactivity
          <button onClick={() => setInactivityWarning(false)} style={{
            background: 'none', border: 'none', color: '#0f172a', fontSize: 18, cursor: 'pointer', marginLeft: 8,
          }}>&times;</button>
        </div>
      )}

      {/* ── Chat Panel ── */}
      {authState.isAuthenticated && (
        <ChatPanel
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          userId={authState.user?.userId || ''}
          activeSessionId={activeSessionId}
          onSessionChange={setActiveSessionId}
          onSessionsUpdate={setChatSessions}
          scoredPhotos={results}
          scoringMode={selectedMode}
          prismEntitlements={TIER_ENTITLEMENTS[userTier].prism}
          onUpgrade={() => setShowUpgrade(true)}
        />
      )}

      {/* ── Feedback Button (fixed, bottom-left) ── */}
      <div style={{
        position: 'fixed', bottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
        left: 16, zIndex: 50,
      }}>
        <button onClick={() => { setShowFeedback(true); setFeedbackSent(false); }} style={{
          background: C.surface, border: `1px solid ${C.divider}`, borderRadius: 20,
          padding: '6px 14px', color: C.muted, fontSize: 12, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          opacity: 0.7, transition: 'opacity 0.2s',
        }}
        onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={e => (e.currentTarget.style.opacity = '0.7')}
        title="Report a bug or send feedback">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          Feedback
        </button>
      </div>

      {/* ── Feedback Modal ── */}
      {showFeedback && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={e => { if (e.target === e.currentTarget) setShowFeedback(false); }}>
          <div style={{
            background: C.bg, border: `1px solid ${C.divider}`, borderRadius: 16,
            padding: 24, width: 'min(440px, 100%)', maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.teal }}>
                {feedbackSent ? 'Thanks!' : 'Report a Bug / Send Feedback'}
              </h3>
              <button onClick={() => setShowFeedback(false)} style={{
                background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer',
              }}>&times;</button>
            </div>

            {feedbackSent ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.teal} strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <p style={{ fontSize: 15, color: C.text, margin: '0 0 8px' }}>Your feedback has been submitted.</p>
                <p style={{ fontSize: 13, color: C.secondary, margin: 0 }}>We'll review it and follow up if needed. Thank you for helping improve FRAME!</p>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: C.secondary, margin: '0 0 12px', lineHeight: 1.5 }}>
                  Found a bug? Have a suggestion? Let us know. Attach a screenshot or file to help us understand the issue.
                </p>

                {/* Text area */}
                <textarea
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  placeholder="Describe the bug or feedback..."
                  rows={4}
                  maxLength={2000}
                  style={{
                    width: '100%', background: C.surface, border: `1px solid ${C.divider}`,
                    borderRadius: 10, padding: 12, color: C.text, fontSize: 14,
                    resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                    minHeight: 80, maxHeight: 200, boxSizing: 'border-box',
                  }}
                />
                <div style={{ fontSize: 11, color: C.muted, textAlign: 'right', marginTop: 2 }}>
                  {feedbackText.length}/2000
                </div>

                {/* File upload */}
                <input
                  ref={feedbackFileRef}
                  type="file"
                  accept="image/*,.pdf,.txt,.log"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => {
                    if (e.target.files) {
                      setFeedbackFiles(prev => [...prev, ...Array.from(e.target.files!)].slice(0, 5));
                    }
                  }}
                />
                <button onClick={() => feedbackFileRef.current?.click()} style={{
                  background: C.surface, border: `1px solid ${C.divider}`, borderRadius: 8,
                  padding: '8px 14px', color: C.secondary, fontSize: 13, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, width: '100%',
                  justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  Attach Screenshot or File {feedbackFiles.length > 0 && `(${feedbackFiles.length})`}
                </button>

                {/* Attached files preview */}
                {feedbackFiles.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {feedbackFiles.map((f, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 4, background: C.surface,
                        border: `1px solid ${C.divider}`, borderRadius: 6, padding: '4px 8px',
                        fontSize: 12, color: C.secondary,
                      }}>
                        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.name}
                        </span>
                        <span style={{ color: C.muted }}>({(f.size / 1024).toFixed(0)}KB)</span>
                        <button onClick={() => setFeedbackFiles(prev => prev.filter((_, j) => j !== i))} style={{
                          background: 'none', border: 'none', color: C.error, fontSize: 14, cursor: 'pointer', padding: '0 2px',
                        }}>&times;</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Device context (auto-included) */}
                <div style={{
                  marginTop: 12, padding: 8, background: C.surface, borderRadius: 8,
                  fontSize: 11, color: C.muted, lineHeight: 1.6,
                }}>
                  <strong style={{ color: C.secondary }}>Auto-included context:</strong><br />
                  Device: {navigator.userAgent.slice(0, 60)}...<br />
                  Screen: {typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'N/A'} &middot; Tier: {userTier} &middot; Mode: {selectedMode}
                </div>

                {/* Submit */}
                <button
                  disabled={!feedbackText.trim() || feedbackSending}
                  onClick={async () => {
                    setFeedbackSending(true);
                    try {
                      const apiUrl = import.meta.env.VITE_API_URL || '';
                      const session = await WebAuthService.getSession();
                      const token = session?.tokens?.idToken?.toString() || '';

                      // Build JSON payload with text + context
                      const payload = {
                        message: feedbackText.trim(),
                        context: {
                          userAgent: navigator.userAgent,
                          screen: `${window.innerWidth}x${window.innerHeight}`,
                          tier: userTier,
                          mode: selectedMode,
                          timestamp: new Date().toISOString(),
                          userId: authState.user?.userId || 'anonymous',
                          email: authState.user?.email || '',
                        },
                      };

                      // Try API endpoint first; fall back to email
                      try {
                        const res = await fetch(`${apiUrl}/feedback`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: JSON.stringify(payload),
                        });
                        if (!res.ok) throw new Error('API unavailable');
                      } catch {
                        // Fallback: open email with text content (files can't be attached via mailto)
                        const subject = encodeURIComponent('FRAME Bug Report / Feedback');
                        const body = encodeURIComponent(
                          `${feedbackText.trim()}\n\n---\nDevice: ${navigator.userAgent}\nScreen: ${window.innerWidth}x${window.innerHeight}\nTime: ${new Date().toISOString()}\nTier: ${userTier}\nMode: ${selectedMode}\n${feedbackFiles.length > 0 ? `\nNote: ${feedbackFiles.length} file(s) were attached but could not be sent via email. Please reply to request them.\n` : ''}`
                        );
                        window.open(`mailto:support@sandstonecloud.com?subject=${subject}&body=${body}`, '_blank');
                      }

                      setFeedbackSent(true);
                      setFeedbackText('');
                      setFeedbackFiles([]);
                    } catch (err) {
                      console.error('[FRAME Feedback]', err);
                    } finally {
                      setFeedbackSending(false);
                    }
                  }}
                  style={{
                    marginTop: 16, width: '100%', padding: '12px 0', borderRadius: 10,
                    background: feedbackText.trim() && !feedbackSending
                      ? `linear-gradient(135deg, ${C.teal} 0%, ${C.mid} 50%, ${C.blue} 100%)`
                      : C.surface,
                    color: feedbackText.trim() && !feedbackSending ? C.white : C.muted,
                    border: feedbackText.trim() && !feedbackSending ? 'none' : `1px solid ${C.divider}`,
                    fontSize: 14, fontWeight: 600, cursor: feedbackText.trim() && !feedbackSending ? 'pointer' : 'default',
                  }}
                >
                  {feedbackSending ? 'Sending...' : 'Submit Feedback'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {/* ─── Version identifier (always visible, bottom center) ─── */}
      <div style={{ position: 'fixed', bottom: 4, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: C.muted, opacity: 0.45, letterSpacing: '0.5px', pointerEvents: 'none', zIndex: 1 }}>
        v{__APP_VERSION__} {__COMMIT_HASH__}
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────
// All responsive via clamp/min/max — no media queries needed.
// Safe areas handled via env() for iOS notch/dynamic island.
// Touch targets ≥ 44px on all interactive elements.
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0f172a 0%, #0c1220 40%, #0e1528 100%)',
  },

  // ── Header ──
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: 'max(16px, env(safe-area-inset-top, 16px)) clamp(12px, 3vw, 32px) 12px',
    position: 'sticky', top: 0, zIndex: 50,
    backgroundColor: 'rgba(15,23,42,0.9)',
    borderBottom: `1px solid ${C.divider}`,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    gap: 8, flexWrap: 'wrap' as const,
  },
  brand: {
    fontSize: 'clamp(22px, 4vw, 28px)', fontWeight: 800, color: C.text,
    letterSpacing: 3, lineHeight: 1, margin: 0,
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  brandSub: { fontSize: 12, color: C.secondary, margin: '2px 0 0' },
  privacyLink: {
    fontSize: 14, color: C.teal, textDecoration: 'none',
    padding: '12px 0', minWidth: 44, textAlign: 'right' as const,
  },
  headerLink: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 'clamp(13px, 2.5vw, 15px)', fontWeight: 500, color: C.teal, textDecoration: 'none',
    padding: '6px 4px', minHeight: 44, lineHeight: 1,
    cursor: 'pointer', border: 'none', background: 'none',
    fontFamily: "'Inter', system-ui, sans-serif",
    verticalAlign: 'middle',
  },

  // ── Main container ──
  main: {
    maxWidth: 680, margin: '0 auto',
    padding: '0 clamp(12px, 4vw, 24px) max(32px, env(safe-area-inset-bottom, 32px))',
  },

  // ── Hero ──
  hero: { textAlign: 'center' as const, padding: 'clamp(28px, 6vw, 56px) 0 20px' },
  heroTitle: {
    fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 700, color: C.text,
    lineHeight: 1.2, margin: '0 0 14px',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  heroBody: {
    fontSize: 'clamp(15px, 2.5vw, 18px)', color: C.secondary,
    lineHeight: 1.6, maxWidth: 520, margin: '0 auto',
  },

  // ── Cards ──
  card: {
    backgroundColor: C.surface, borderRadius: 20,
    padding: 'clamp(16px, 3vw, 24px)',
    marginBottom: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
    border: `1px solid ${C.divider}`,
  },
  sectionLabel: {
    fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 12px',
    letterSpacing: 0.3,
  },
  sectionDivider: {
    height: 1, backgroundColor: C.divider, margin: '20px 0',
  },

  // ── Dropzone ──
  dropzone: {
    border: `2px dashed ${C.surfaceHover}`, borderRadius: 16,
    padding: 'clamp(20px, 4vw, 44px) 16px', textAlign: 'center' as const,
    cursor: 'pointer', backgroundColor: 'rgba(30,41,59,0.6)',
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  uploadIcon: { fontSize: 36, marginBottom: 12 },
  dropzoneTitle: { fontSize: 17, fontWeight: 600, color: C.text, margin: '0 0 6px' },
  dropzoneCount: { fontSize: 17, fontWeight: 600, color: C.teal, margin: '0 0 4px' },
  dropzoneHint: { fontSize: 14, color: C.secondary, margin: 0, lineHeight: 1.5 },
  thumbStrip: {
    display: 'flex', gap: 6, justifyContent: 'center',
    marginTop: 14, flexWrap: 'wrap' as const,
  },
  thumbImg: {
    width: 'clamp(40px, 10vw, 52px)', height: 'clamp(40px, 10vw, 52px)',
    borderRadius: 8, objectFit: 'cover' as const,
  },
  thumbMore: {
    width: 'clamp(40px, 10vw, 52px)', height: 'clamp(40px, 10vw, 52px)',
    borderRadius: 8, backgroundColor: C.divider,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 600, color: C.secondary,
  },

  // ── Mode chips (horizontal scrollable on small screens) ──
  modeChipScroll: {
    overflowX: 'auto' as const, marginLeft: -4, marginRight: -4,
    paddingBottom: 4, WebkitOverflowScrolling: 'touch',
  },
  modeChipRow: {
    display: 'flex', gap: 8, paddingLeft: 4, paddingRight: 4,
    width: 'max-content',
  },
  modeChip: {
    padding: '10px 18px', borderRadius: 999,
    border: 'none', display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 14, transition: 'all 0.15s',
    minHeight: 44, whiteSpace: 'nowrap' as const,
    WebkitTapHighlightColor: 'transparent',
  },
  lockBadge: { fontSize: 11, lineHeight: 1, opacity: 0.7 },
  tagline: {
    fontStyle: 'italic', fontSize: 13, color: C.secondary,
    margin: '8px 0 0', lineHeight: 1.4,
  },

  // ── Weight bars ──
  weightGrid: {
    display: 'flex', flexDirection: 'column' as const, gap: 12,
  },
  weightRow: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  weightLabel: {
    fontSize: 13, color: C.text, flex: '1 1 auto', minWidth: 0,
    whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis',
    lineHeight: 1.3,
  },
  weightBarTrack: {
    width: 'clamp(60px, 15vw, 100px)', height: 6, backgroundColor: C.divider,
    borderRadius: 3, flexShrink: 0, overflow: 'hidden',
  },
  weightBarFill: {
    height: '100%', background: `linear-gradient(90deg, ${C.teal}, ${C.mid})`, borderRadius: 3,
    transition: 'width 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  weightPct: {
    fontSize: 12, fontWeight: 600, color: C.teal, width: 36,
    textAlign: 'right' as const, flexShrink: 0,
  },

  // ── Vibe input (ENLARGED chat box) ──
  vibeInput: {
    width: '100%',
    minHeight: 220,
    maxHeight: 400,
    padding: 16,
    fontSize: 16,
    lineHeight: '24px',
    color: C.text,
    backgroundColor: 'rgba(15,23,42,0.6)',
    border: `1.5px solid ${C.divider}`,
    borderRadius: 16,
    resize: 'vertical' as const,
    outline: 'none',
    fontFamily: 'inherit',
    WebkitAppearance: 'none' as const,
    display: 'block',
    boxSizing: 'border-box' as const,
  },
  chatHint: {
    fontSize: 12, color: C.secondary, margin: '8px 0 0',
    fontStyle: 'italic', lineHeight: 1.4,
  },

  // ── Count selector ──
  countRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, marginBottom: 6,
  },
  countLabel: { fontSize: 16, color: C.text },
  countInput: {
    width: 64, fontSize: 18, fontWeight: 700, color: C.teal,
    textAlign: 'center' as const, border: 'none',
    borderBottom: `2px solid ${C.teal}`, backgroundColor: 'transparent',
    padding: '4px 0', outline: 'none',
    WebkitAppearance: 'none' as const, MozAppearance: 'textfield' as const,
    minHeight: 44,
  },
  countHint: {
    fontSize: 13, color: C.secondary, textAlign: 'center' as const,
    margin: '0 0 20px', lineHeight: 1.4,
  },

  // ── Score CTA ──
  scoreCTA: {
    width: '100%', padding: '16px 24px', fontSize: 18, fontWeight: 600,
    color: C.white,
    background: `linear-gradient(135deg, ${C.teal} 0%, ${C.mid} 50%, ${C.blue} 100%)`,
    backgroundSize: '100% auto',
    border: 'none',
    borderRadius: 999, transition: 'all 0.2s',
    boxShadow: '0 2px 16px rgba(80,184,168,0.30), inset 0 1px 0 rgba(255,255,255,0.12)',
    minHeight: 56, letterSpacing: 0.3,
    WebkitTapHighlightColor: 'transparent',
  },

  // ── Progress ──
  progressTrack: {
    width: '100%', height: 6, backgroundColor: C.divider,
    borderRadius: 3, overflow: 'hidden',
  },
  progressFill: {
    height: '100%', background: `linear-gradient(90deg, ${C.teal}, ${C.blue})`, borderRadius: 3,
    transition: 'width 0.3s ease',
  },
  privacyNote: {
    fontSize: 13, color: C.secondary, textAlign: 'center' as const,
    margin: '10px 0 0',
  },

  // ── Histogram ──
  histogram: {
    display: 'flex', alignItems: 'flex-end', gap: 4,
    height: 100, marginBottom: 16, padding: '0 4px',
  },
  histBarWrap: {
    flex: 1, display: 'flex', flexDirection: 'column' as const,
    alignItems: 'center', height: '100%', justifyContent: 'flex-end',
  },
  histBar: {
    width: '100%', borderRadius: 2, minHeight: 3,
    transition: 'height 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  histLabel: { fontSize: 10, color: C.secondary, marginTop: 4 },
  histCount: { fontSize: 9, color: C.teal, fontWeight: 600, marginBottom: 2 },

  // ── Photo grid ──
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(80px, 20vw, 120px), 1fr))',
    gap: 'clamp(6px, 1.5vw, 10px)',
  },
  photoCard: {
    position: 'relative' as const, borderRadius: 12, overflow: 'hidden',
    aspectRatio: '1', border: '2.5px solid transparent',
    transition: 'border-color 0.2s, opacity 0.2s, transform 0.15s',
    backgroundColor: C.bg,
  },
  photoImg: {
    width: '100%', height: '100%', objectFit: 'cover' as const,
    display: 'block',
  },
  scoreBadge: {
    position: 'absolute' as const, bottom: 6, right: 6,
    padding: '3px 8px', borderRadius: 8,
    fontSize: 12, fontWeight: 700, color: C.white,
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    lineHeight: 1.3,
  },

  // ── Modal ──
  modalOverlay: {
    position: 'fixed' as const, inset: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 'clamp(8px, 2vw, 24px)',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
  },
  modalCard: {
    backgroundColor: C.surface, borderRadius: 24,
    padding: 'clamp(16px, 3vw, 24px)',
    width: '100%',
    maxHeight: '90dvh',
    overflowY: 'auto' as const,
    boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
    position: 'relative' as const,
    WebkitOverflowScrolling: 'touch',
    border: `1px solid ${C.divider}`,
  },
  modal: {
    backgroundColor: C.surface, borderRadius: 24,
    width: '100%', maxWidth: 420,
    maxHeight: '90dvh',
    overflowY: 'auto' as const,
    boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
    position: 'relative' as const,
    WebkitOverflowScrolling: 'touch',
    border: `1px solid ${C.divider}`,
  },
  modalClose: {
    position: 'absolute' as const, top: 12, right: 12, zIndex: 10,
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.45)', color: '#fff',
    fontSize: 17, fontWeight: 600, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    border: 'none', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  modalImg: {
    width: '100%', maxHeight: 'clamp(220px, 50vh, 400px)',
    objectFit: 'contain' as const, display: 'block',
    backgroundColor: '#000',
  },
  modalBody: { padding: 'clamp(16px, 3vw, 24px)' },
  modalScoreRow: { textAlign: 'center' as const, marginBottom: 20 },
  modalComposite: { fontSize: 48, fontWeight: 800, color: C.teal, display: 'block' },
  modalPctLabel: { display: 'block', fontSize: 14, color: C.secondary, marginTop: 4 },

  // ── Dimension bars (in modal) ──
  dimRow: { marginBottom: 16 },
  dimHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 5,
  },
  dimLabel: { fontSize: 14, fontWeight: 500, color: C.text },
  dimScore: { fontSize: 16, fontWeight: 700 },
  dimTrack: {
    height: 6, backgroundColor: C.divider, borderRadius: 3, overflow: 'hidden',
  },
  dimFill: {
    height: '100%', borderRadius: 3,
  },
  dimNote: {
    fontSize: 12, color: C.secondary, fontStyle: 'italic', margin: '4px 0 0',
    lineHeight: 1.3,
  },

  // ── Badges ──
  badge: {
    display: 'inline-block', backgroundColor: 'rgba(80,184,168,0.15)', borderRadius: 8,
    padding: '6px 12px', fontSize: 13, fontWeight: 500, color: C.teal,
    marginTop: 8, marginRight: 6, lineHeight: 1.3,
    boxShadow: 'inset 0 1px 0 rgba(80,184,168,0.08)',
  },
  detractorBadge: { backgroundColor: 'rgba(239,68,68,0.08)', color: C.error },

  // ── CTA section ──
  ctaSection: { textAlign: 'center' as const, padding: '36px 0 16px' },
  ctaText: { fontSize: 16, fontWeight: 600, color: C.text, margin: '0 0 4px' },
  ctaSubtext: { fontSize: 14, color: C.secondary, margin: 0 },

  // ── Footer ──
  footer: {
    textAlign: 'center' as const,
    padding: '24px 0 max(40px, env(safe-area-inset-bottom, 40px))',
    fontSize: 13, color: C.secondary, lineHeight: 1.6,
  },
};
